/**
 * Instant Cache Service
 * Provides instant access to pre-calculated data
 */

// Import calculation services
import { buildHybridAverageDailySalesByProduct } from '../utils/dailyStockTracker';
import {
  getCoveragePolicy,
  computePrincipalCoverage,
  computeSuggestedReorderQty
} from '../utils/stockReorderPolicy';
import {
  calculateStockMovementData
} from './stockAnalyticsService';
import {
  LOT_STATUS,
  getLotsForProduct,
  flattenPurchaseLineItems,
  dedupeLotsList,
  isLotAvailableForSale,
  startOfLocalDayMs
} from '../utils/lotSystem';
import { mergeExpiredProductsForDisplay } from '../domain/inventory/expiredView';
import { isCoordinatedStockRecalculationActive } from '../domain/inventory/recalculation';
import { expandDiscrepancyManagementForDisplay, isDeviationPending, isMeaningfulEcart } from '../utils/resolveDiscrepancyForEcarts';
import DeviationService from './deviationService';
import { businessDataStorageKey, getCurrentBusinessId, readBestBusinessMergedPayload } from '../utils/sharedCore';
import { mapNamesToKeys } from '../utils/catalogValidation';
import { getFrenchCategoryName } from '../utils/categoryMapping';
import { categories as catalogCategories } from '../catalog/categoryTree';
import { canonicalizeCategoryKey } from '../catalog/legacyAliases';
import { getVisibleProducts } from '../utils/stockVisibility';
import { resolveLowStockThreshold } from '../utils/stockAlertThreshold';
import { readBusinessPayloadFieldsSync } from '../application/read/syncBusinessPayloadRead';
import { getBusinessPayloadRepository } from '../app/di/container';
import {
  isActivePurchaseRow,
  isPurchaseReceiptFullyVoided
} from '../domain/purchases/purchaseVoidStatus';
import {
  publishInstantCacheUpdated,
  publishLegacyWindowEvent,
  publishProductsUpdated,
  publishSalesUpdated
} from '../infrastructure/events/legacyEventBridge';
import { getStorageEngine } from '../app/di/container';
import { kvGet } from '../infrastructure/storage/kvAccess';
import { readStoredCurrentBusiness } from './offlineAuthSession';
import { getSaleTotalAmount } from '../domain/restaurant/restaurantDishSaleService';
import { calculateProfit } from '../domain/finance/profit';
import { summarizeRevenue } from '../domain/finance/revenue';
import { resolveSaleRegisterIncome } from '../domain/sales/checkout';
import { listExpiringLots, classifyExpiryUrgency, getExpiryAlertThreshold } from '../domain/inventory/expiry';
import { calculateInventoryValue } from '../domain/inventory/valuation';
import { readStockSnapshotsSyncForBusiness } from '../domain/inventory/snapshots';
import { projectStockFromLots } from '../domain/inventory/stock';
import {
  aggregateMovementProductStats,
  getMovementProductStats
} from '../domain/inventory/movements/aggregateMovementProductStats';
import {
  indexLotsByProduct,
  indexLotsByProductLocation,
  aggregateLotsMetrics,
  lotProductKey,
  lotProductLocationKey,
  normalizeLotsList as normalizeLotsListShared,
  isLotIncludedInStockMetrics
} from '../domain/inventory/lots/lotsIndex';
import { rankTopProducts } from '../domain/sales/analytics';
import {
  loadSessionsForWorker,
  loadSessionsFromKey,
  workerSessionStorageKey
} from '../utils/workerSessionsStorage';
import { sameProductId } from '../utils/productIdUtils';
import {
  sumAvailableQuantityFromLots,
  sumAvailableValueFromLots
} from '../utils/inventoryAvailability';
import { isRestaurantDishProduct } from '../utils/restaurantMode';

/** Stock page / lots — never plats menu or non-tracked catalog items. */
function isStockTrackedCatalogProduct(product) {
  if (!product) return false;
  if (isRestaurantDishProduct(product)) return false;
  if (product.trackInventory === false) return false;
  return true;
}

function normalizeLotsList(lots) {
  return normalizeLotsListShared(lots);
}

/** FNV-1a over a string — cheap, order-sensitive, good enough as a rebuild guard. */
function hashText(text, seed = 0x811c9dc5) {
  let hash = seed >>> 0;
  for (let i = 0; i < text.length; i += 1) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash >>> 0;
}

/**
 * Content signature for the stock projection.
 * Counts alone missed product edits and transfers that merged into an existing lot
 * (same lot count, same total quantity) — the table then kept showing stale rows.
 */
function stockProjectionSignature(products, lotsArray, stockLocations) {
  let productHash = 0x811c9dc5;
  for (const product of products) {
    if (!product) continue;
    productHash = hashText(
      `${product.id ?? ''}|${product.name ?? ''}|${product.sku ?? ''}|${product.category ?? ''}|` +
        `${product.unit ?? ''}|${product.price ?? ''}|${product.sellingPrice ?? ''}|` +
        `${product.minStock ?? product.minQuantity ?? ''}|${product.updatedAt ?? product.updated_at ?? ''}`,
      productHash
    );
  }
  let lotHash = 0x811c9dc5;
  for (const lot of lotsArray) {
    if (!lot) continue;
    lotHash = hashText(
      `${lot.id ?? ''}|${lot.productId ?? ''}|${lot.location ?? ''}|${lot.remainingQuantity ?? ''}|` +
        `${lot.purchaseUnitPrice ?? ''}|${lot.sellingUnitPrice ?? ''}|${lot.expiryDate ?? ''}|` +
        `${lot.updatedAt ?? lot.updated_at ?? ''}`,
      lotHash
    );
  }
  return [
    products.length,
    lotsArray.length,
    stockLocations.join('|'),
    productHash.toString(36),
    lotHash.toString(36)
  ].join(':');
}

/** Verkaufspreis: Lots (nach Achat) → Achats-Zeilen → Produkt. */
export function resolveSellingUnitPriceForProduct(productId, lots, purchases, product = {}) {
  const lotsArr = normalizeLotsList(lots);
  const active = lotsArr.filter(
    (lot) =>
      sameProductId(lot.productId, productId) &&
      isLotAvailableForSale(lot) &&
      Number(lot.sellingUnitPrice) > 0
  );
  if (active.length > 0) {
    const sorted = [...active].sort(
      (a, b) => new Date(b.purchaseDate || b.createdAt || 0) - new Date(a.purchaseDate || a.createdAt || 0)
    );
    return Number(sorted[0].sellingUnitPrice);
  }

  const flatPurchases = (purchases || []).filter(
    (p) => sameProductId(p.productId, productId) && Number(p.sellingPrice) > 0
  );
  if (flatPurchases.length > 0) {
    const sorted = [...flatPurchases].sort(
      (a, b) => new Date(b.date || b.deliveryDate || 0) - new Date(a.date || a.deliveryDate || 0)
    );
    return Number(sorted[0].sellingPrice);
  }

  for (const purchase of purchases || []) {
    if (!purchase?.products || !Array.isArray(purchase.products)) continue;
    const line = purchase.products.find((item) => sameProductId(item.productId, productId));
    if (line && Number(line.sellingPrice) > 0) {
      return Number(line.sellingPrice);
    }
  }

  const fromProduct = Number(product.sellingUnitPrice || product.sellingPrice || product.price);
  return Number.isFinite(fromProduct) && fromProduct > 0 ? fromProduct : 0;
}

/** Einkaufspreis: gewichteter Mittelwert aus Lots → letzter Lot/Achat → Produkt. */
export function resolvePurchaseUnitPriceForProduct(
  productId,
  lots,
  purchases,
  product = {},
  stockNow = 0,
  stockValue = 0
) {
  if (Number(stockNow) > 0 && Number(stockValue) > 0) {
    return Number(stockValue) / Number(stockNow);
  }

  const lotsArr = normalizeLotsList(lots);
  const withPrice = lotsArr.filter(
    (lot) =>
      sameProductId(lot.productId, productId) &&
      Number(lot.purchaseUnitPrice || lot.purchasePrice) > 0
  );
  if (withPrice.length > 0) {
    const sorted = [...withPrice].sort(
      (a, b) => new Date(b.purchaseDate || b.createdAt || 0) - new Date(a.purchaseDate || a.createdAt || 0)
    );
    return Number(sorted[0].purchaseUnitPrice || sorted[0].purchasePrice);
  }

  const flatPurchases = (purchases || []).filter(
    (p) => sameProductId(p.productId, productId) && Number(p.purchaseUnitPrice) > 0
  );
  if (flatPurchases.length > 0) {
    const sorted = [...flatPurchases].sort(
      (a, b) => new Date(b.date || b.deliveryDate || 0) - new Date(a.date || a.deliveryDate || 0)
    );
    return Number(sorted[0].purchaseUnitPrice);
  }

  for (const purchase of purchases || []) {
    if (!purchase?.products || !Array.isArray(purchase.products)) continue;
    const line = purchase.products.find((item) => sameProductId(item.productId, productId));
    if (line && Number(line.purchaseUnitPrice) > 0) {
      return Number(line.purchaseUnitPrice);
    }
  }

  const fromProduct = Number(product.purchaseUnitPrice || product.purchasePrice);
  return Number.isFinite(fromProduct) && fromProduct > 0 ? fromProduct : 0;
}

class InstantCache {
  constructor() {
    this.cache = new Map();
    this.isInitialized = false;
    this.dataVersion = 0;
    this.lastDataHash = null;
    
    // Debouncing for cache updates
    this.updateTimeout = null;
    this.pendingUpdates = {};
    this.lastUpdateTime = 0;
    this.debounceDelay = 500; // 500ms debounce
    
    // Memoization for filter calculations
    this.filterCache = new Map();
    this.lastFilterHash = null;
    
    // InstantCache is memory-only (ADR-0014). Legacy KV dumps are purged in IndexedDbKvBackend.
    this._cachedBusinessId = null;
  }

  /**
   * POS boot sync — preload, connexion, téléchargement localStorage/IndexedDB.
   * Minimal: one in-memory init per payload hash; no KV dump; no repeat warm.
   */
  syncPosBootData(data, { businessId = null, force = false, fastBoot = true } = {}) {
    const bid = businessId || this._getCurrentBusinessId();
    if (bid && this._cachedBusinessId && bid !== this._cachedBusinessId) {
      this.clear();
    }
    if (bid) {
      this._cachedBusinessId = bid;
    }

    const purchasesNorm = Array.isArray(data?.purchases)
      ? data.purchases
      : data?.purchases && typeof data.purchases === 'object'
        ? Object.values(data.purchases)
        : [];
    const lotsNorm = data?.lots
      ? Array.isArray(data.lots)
        ? data.lots
        : Object.values(data.lots || {})
      : [];
    const payload = { ...data, purchases: purchasesNorm, lots: lotsNorm };
    const cachedLots = this.cache.get('lots') || [];
    const bootHash = this.calculateDataHash({
      products: data?.products || [],
      lots: lotsNorm,
      purchases: purchasesNorm,
      sales: data?.sales || [],
      suppliers: data?.suppliers || []
    });

    // Same payload already warm — never re-init / re-apply (avoids duplicate enrich+stock).
    if (!force && this.isInitialized && this._posBootHash === bootHash && this.cache.has('products')) {
      return;
    }

    const shouldFullInit =
      force ||
      !this.isInitialized ||
      !this.cache.has('products') ||
      (lotsNorm.length > 0 && cachedLots.length === 0);

    if (shouldFullInit) {
      this.initialize(payload, { businessId: bid, force: true, fastBoot });
      this._posBootHash = bootHash;
      return;
    }

    this._posBootHash = bootHash;
  }

  _scheduleDeferredHeavyInit(data) {
    if (this._deferredHeavyInitScheduled) return;
    this._deferredHeavyInitScheduled = true;

    void (async () => {
      try {
        const {
          resolveBootSurface,
          getDeferredWarmPlan
        } = await import('../infrastructure/startup/bootSurface');
        const {
          scheduleCancellableIdle,
          ensureBootIdleNavigationCancel,
          getBootIdleGeneration
        } = await import('../infrastructure/startup/bootIdleScheduler');

        ensureBootIdleNavigationCancel();
        const surface = resolveBootSurface({
          businessId: data?.businessId || this._cachedBusinessId
        });
        const plan = getDeferredWarmPlan(surface);
        const genAtSchedule = getBootIdleGeneration();

        scheduleCancellableIdle(
          () => {
            this._deferredHeavyInitScheduled = false;

            if (genAtSchedule !== getBootIdleGeneration()) {
              // Route changed — drop this warm. Next initialize/fastBoot will schedule again.
              // Do NOT reschedule here: that kept InstantCache "persisting" across Inventaire.
              return;
            }

            const enrichedProducts = this.cache.get('products') || [];
            const lotsNorm = this.cache.get('lots') || [];
            const purchasesNorm = this.cache.get('purchases') || [];
            const payload = {
              ...data,
              products: enrichedProducts,
              lots: lotsNorm,
              purchases: purchasesNorm
            };

            // Slice heavy work across idle callbacks — one long block freezes UI.
            const steps = [];
            if (plan.filters && enrichedProducts.length > 0) {
              steps.push(() => this.preCalculateFilters(enrichedProducts));
            }
            if (plan.reports) {
              steps.push(() => this.preCalculateReportsData(payload));
            }
            if (plan.stock) {
              steps.push(() => this.preCalculateStockData(payload));
            }
            if (plan.managerDashboard) {
              steps.push(() => this.preCalculateManagerDashboard(payload));
            }
            if (plan.workerDashboard) {
              steps.push(() => {
                this.preCalculateWorkerDashboard?.(payload);
                this.preCalculateDashboardSpecificData?.(payload);
              });
            }
            if (plan.sales) {
              steps.push(() => this.preCalculateSalesData?.(payload));
            }
            if (plan.purchases) {
              steps.push(() => this.preCalculatePurchasesData?.(payload));
            }

            if (steps.length === 0) return;

            let stepIndex = 0;
            const runStep = () => {
              if (genAtSchedule !== getBootIdleGeneration()) return;
              try {
                steps[stepIndex]?.();
              } catch (error) {
                if (process.env.NODE_ENV === 'development') {
                  console.warn('[instantCache] deferred heavy step failed:', error?.message || error);
                }
              }
              stepIndex += 1;
              if (stepIndex < steps.length) {
                scheduleCancellableIdle(runStep, {
                  timeout: 2500,
                  label: `heavyInit:${surface}:step${stepIndex}`
                });
              } else {
                this._throttledNotifyCacheUpdate();
              }
            };
            runStep();
          },
          { timeout: 5000, label: `heavyInit:${surface}` }
        );
      } catch (error) {
        this._deferredHeavyInitScheduled = false;
        if (process.env.NODE_ENV === 'development') {
          console.warn('[instantCache] deferred heavy init schedule failed:', error?.message || error);
        }
      }
    })();
  }

  _scheduleDeferredProductPricing(products, purchases, lots) {
    void (async () => {
      try {
        const {
          scheduleCancellableIdle,
          ensureBootIdleNavigationCancel,
          getBootIdleGeneration
        } = await import('../infrastructure/startup/bootIdleScheduler');
        ensureBootIdleNavigationCancel();
        const genAtSchedule = getBootIdleGeneration();
        scheduleCancellableIdle(
          () => {
            if (genAtSchedule !== getBootIdleGeneration()) return;
            try {
              if (products?.length > 0) {
                this.preCalculateProductPricing(products, purchases, lots);
                this._throttledNotifyCacheUpdate();
              }
            } catch (error) {
              if (process.env.NODE_ENV === 'development') {
                console.warn('[instantCache] deferred pricing failed:', error?.message || error);
              }
            }
          },
          { timeout: 4000, label: 'deferredPricing' }
        );
      } catch {
        /* best-effort */
      }
    })();
  }

  /**
   * Initialize cache with data
   */
  initialize(data, { businessId = null, force = false, fastBoot = false } = {}) {
    const purchasesNorm = Array.isArray(data.purchases)
      ? data.purchases
      : data.purchases && typeof data.purchases === 'object'
        ? Object.values(data.purchases)
        : [];
    const lotsNorm = data.lots
      ? Array.isArray(data.lots)
        ? data.lots
        : Object.values(data.lots || {})
      : [];
    const dataForHash = { ...data, purchases: purchasesNorm, lots: lotsNorm };

    // Check if data has changed
    const currentHash = this.calculateDataHash(dataForHash);
    if (!force && this.lastDataHash === currentHash && this.isInitialized) {
      return; // Data hasn't changed, no need to reinitialize
    }

    const bid = businessId || this._getCurrentBusinessId();
    if (bid) {
      this._cachedBusinessId = bid;
    }

    this.cache.clear();
    this.dataVersion++;
    this.lastDataHash = currentHash;

    const productList = Array.isArray(data.products) ? data.products : [];
    // fastBoot: paint catalog immediately; enrich/prices at idle. Stock projection stays sync (POS sell).
    const deferEnrich = Boolean(fastBoot);
    let enrichedProducts = productList;

    if (!deferEnrich && productList.length > 0) {
      enrichedProducts = this.enrichProductsWithStockData(
        productList,
        lotsNorm,
        purchasesNorm,
        data.stockLocations
      );
    }

    this.cache.set('products', enrichedProducts);
    this.cache.set('productsCount', enrichedProducts.length);

    // POS-critical: stock projection once on the sync path (never wait for idle enrich).
    this.preCalculateStockData({
      products: productList,
      lots: lotsNorm,
      purchases: purchasesNorm,
      stockLocations: data.stockLocations || ['Principal']
    });

    const sortedForPos = [...enrichedProducts].sort((a, b) => {
      const aUpdated = new Date(a.updatedAt || a.createdAt || 0);
      const bUpdated = new Date(b.updatedAt || b.createdAt || 0);
      return bUpdated - aUpdated;
    });

    // One sorted list, two keys (API compat) — no duplicate arrays.
    this.cache.set('filter_', sortedForPos);
    this.cache.set('sortedProducts', sortedForPos);

    if (fastBoot) {
      this._scheduleDeferredProductEnrich(
        productList,
        lotsNorm,
        purchasesNorm,
        data.stockLocations
      );
    } else if (productList.length > 0) {
      this.preCalculateProductPricing(enrichedProducts, purchasesNorm, lotsNorm);
      this.preCalculateFilters(enrichedProducts);
    }

    if (data.suppliers) {
      this.cache.set('suppliers', data.suppliers);
    }

    this.cache.set('lots', lotsNorm);
    this.cache.set('purchases', purchasesNorm);
    this.cache.set('purchasesCount', purchasesNorm.length);

    this.isInitialized = true;

    const bootPayload = { ...data, lots: lotsNorm, purchases: purchasesNorm, products: enrichedProducts };

    if (fastBoot) {
      // Surface-scoped idle warm only — never dump derived models to KV.
      this._scheduleDeferredHeavyInit(bootPayload);
    } else {
      this.preCalculateReportsData(bootPayload);
      this.preCalculateDashboardData(bootPayload);
      this.preCalculateSalesData(bootPayload);
      this.preCalculatePurchasesData(bootPayload);
    }

    this._throttledNotifyCacheUpdate();
    this.notifyCardUpdates(bootPayload);
  }

  /** Idle: product enrich + pricing after fastBoot painted the catalog (stock already sync). */
  _scheduleDeferredProductEnrich(products, lots, purchases, stockLocations) {
    void (async () => {
      try {
        const {
          scheduleCancellableIdle,
          ensureBootIdleNavigationCancel,
          getBootIdleGeneration
        } = await import('../infrastructure/startup/bootIdleScheduler');
        ensureBootIdleNavigationCancel();
        const genAtSchedule = getBootIdleGeneration();
        scheduleCancellableIdle(
          () => {
            if (genAtSchedule !== getBootIdleGeneration()) return;
            try {
              if (!products?.length) return;
              const enriched = this.enrichProductsWithStockData(
                products,
                lots,
                purchases,
                stockLocations
              );
              this.cache.set('products', enriched);
              this.cache.set('productsCount', enriched.length);
              const sortedForPos = [...enriched].sort((a, b) => {
                const aUpdated = new Date(a.updatedAt || a.createdAt || 0);
                const bUpdated = new Date(b.updatedAt || b.createdAt || 0);
                return bUpdated - aUpdated;
              });
              this.cache.set('filter_', sortedForPos);
              this.cache.set('sortedProducts', sortedForPos);
              this._scheduleDeferredProductPricing(enriched, purchases, lots);
              this.dataVersion++;
              this._throttledNotifyCacheUpdate();
              this.notifyCardUpdates({
                products: enriched,
                lots,
                purchases
              });
            } catch {
              /* best-effort enrich */
            }
          },
          { timeout: 4500, label: 'deferredProductEnrich' }
        );
      } catch {
        /* best-effort */
      }
    })();
  }

  /**
   * Calculate hash of data to detect changes
   */
  _quickCollectionSig(items, fields, step = 25) {
    if (!items?.length) return '0';
    const stride = Math.max(1, Math.floor(items.length / step));
    let sig = String(items.length);
    if (fields.includes('remainingQuantity')) {
      const totalRq = items.reduce((sum, item) => sum + (Number(item?.remainingQuantity) || 0), 0);
      sig += `|rq:${totalRq}`;
    }
    if (fields.includes('stockNow')) {
      const totalStock = items.reduce(
        (sum, item) =>
          sum +
          (Number(item?.stockNow) ||
            Number(item?.calculatedQuantity) ||
            Number(item?.stockQuantity) ||
            0),
        0
      );
      sig += `|st:${totalStock}`;
    }
    for (let i = 0; i < items.length; i += stride) {
      const item = items[i];
      if (!item || typeof item !== 'object') continue;
      sig += '|';
      for (const field of fields) {
        sig += `${item[field] ?? ''}:`;
      }
    }
    return sig;
  }

  calculateDataHash(data) {
    const productsHash = data.products
      ? this._quickCollectionSig(data.products, ['id', 'updatedAt', 'stockNow'])
      : '0';
    const suppliersHash = data.suppliers
      ? this._quickCollectionSig(data.suppliers, ['id', 'updatedAt'])
      : '0';

    let lotsHash = '0';
    if (data.lots) {
      const lotsArr = Array.isArray(data.lots) ? data.lots : Object.values(data.lots || {});
      lotsHash = this._quickCollectionSig(lotsArr, [
        'id',
        'remainingQuantity',
        'updatedAt',
        'purchaseUnitPrice'
      ]);
    }

    const purchasesArr = Array.isArray(data.purchases)
      ? data.purchases
      : data.purchases && typeof data.purchases === 'object'
        ? Object.values(data.purchases)
        : [];
    const purchasesHash = this._quickCollectionSig(purchasesArr, [
      'id',
      'purchaseId',
      'quantity',
      'total',
      'updatedAt'
    ]);

    return `${productsHash}_${suppliersHash}_${lotsHash}_${purchasesHash}`;
  }

  /**
   * Calculate hash for products to detect filter changes
   */
  _calculateProductsHash(products) {
    const str = JSON.stringify(products.map(p => ({
      id: p.id,
      category: p.category,
      subcategory: p.subcategory,
      type: p.type,
      updatedAt: p.updatedAt,
      createdAt: p.createdAt
    })));
    return this._simpleHash(str);
  }

  /**
   * Simple hash function
   */
  _simpleHash(str) {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return hash.toString();
  }

  /**
   * Check if products array has actually changed
   */
  _productsChanged(currentProducts, newProducts) {
    if (currentProducts.length !== newProducts.length) {
      return true;
    }
    
    // Check if any product has changed
    for (let i = 0; i < currentProducts.length; i++) {
      const current = currentProducts[i];
      const updated = newProducts[i];
      
      if (!current || !updated || 
          current.id !== updated.id ||
          current.updatedAt !== updated.updatedAt ||
          current.category !== updated.category ||
          current.subcategory !== updated.subcategory ||
          current.type !== updated.type) {
        return true;
      }
    }
    
    return false;
  }

  _lotsChanged(currentLots, newLots) {
    const cur = Array.isArray(currentLots) ? currentLots : Object.values(currentLots || {});
    const next = Array.isArray(newLots) ? newLots : Object.values(newLots || {});
    if (cur.length !== next.length) {
      return true;
    }

    for (let i = 0; i < cur.length; i += 1) {
      const current = cur[i];
      const updated = next[i];
      if (
        !current ||
        !updated ||
        current.id !== updated.id ||
        current.updatedAt !== updated.updatedAt ||
        current.remainingQuantity !== updated.remainingQuantity
      ) {
        return true;
      }
    }

    return false;
  }

  /**
   * Notify that cache has been updated
   */
  notifyCacheUpdate() {
    publishInstantCacheUpdated({
      version: this.dataVersion,
      stats: this.getStats(),
      isInitialized: this.isInitialized
    });
  }

  /**
   * Throttled cache update notification
   */
  _throttledNotifyCacheUpdate() {
    if (this._notificationTimeout) {
      clearTimeout(this._notificationTimeout);
    }
    
    this._notificationTimeout = setTimeout(() => {
      // Use requestAnimationFrame for better performance
      requestAnimationFrame(() => {
        this.notifyCacheUpdate();
      });
    }, 100); // 100ms delay to batch notifications
  }

  /**
   * Notify specific card updates
   */
  notifyCardUpdates(data) {
    // Notify sales updates (affects cash register card)
    if (data.sales) {
      publishSalesUpdated({
        salesCount: data.sales.length,
        timestamp: new Date().toISOString()
      });
    }

    // Notify ecart updates (affects gestion card)
    if (data.discrepancyManagement) {
      const allEcarts = [
        ...(data.discrepancyManagement.selfDeviations || []),
        ...(data.discrepancyManagement.handoverDeviations || [])
      ];
      const openEcarts = allEcarts.filter(
        (ecart) => isDeviationPending(ecart) && isMeaningfulEcart(ecart)
      );

      publishLegacyWindowEvent('ecartUpdated', {
        businessId: this._cachedBusinessId || this._getCurrentBusinessId() || null,
        totalEcarts: allEcarts.length,
        openEcarts: openEcarts.length,
        timestamp: new Date().toISOString()
      });
    }

    // Notify product updates (affects operations card)
    if (data.products || data.lots) {
      publishProductsUpdated({
        productsCount: data.products?.length || 0,
        lotsCount: Object.keys(data.lots || {}).length,
        timestamp: new Date().toISOString()
      });
    }

    // Notify general data changes (affects notifications card)
    publishLegacyWindowEvent('notificationUpdated', {
      hasNewData: true,
      timestamp: new Date().toISOString()
    });
  }

  /**
   * True after a successful business load into this cache.
   * Empty catalogs (0 products) are valid — new businesses must not stay "cold".
   */
  isReady() {
    return Boolean(this.isInitialized && this.cache.has('products'));
  }

  /**
   * Pre-calculate all common filter combinations with memoization
   */
  preCalculateFilters(products) {
    // Check if filters are already calculated for this data
    const currentHash = this._calculateProductsHash(products);
    if (this.lastFilterHash === currentHash && this.filterCache.size > 0) {
      return;
    }
    
    // Unique subcategory/type values for non-category filter caches
    const subcategories = [...new Set(products.map(p => p.subcategory).filter(Boolean))];
    const types = [...new Set(products.map(p => p.type).filter(Boolean))];
    
    // Sort products by latest updated (newest first) before caching
    const sortedProducts = products.sort((a, b) => {
      const aUpdated = new Date(a.updatedAt || a.createdAt || 0);
      const bUpdated = new Date(b.updatedAt || b.createdAt || 0);
      return bUpdated - aUpdated;
    });
    
    
    // Debug: Show sample product categories
    
    // Cache all products (no filters) - sorted by latest updated
    this.cache.set('filter_', sortedProducts);
    
    // Update filter hash for memoization
    this.lastFilterHash = currentHash;

    // Bucket by canonical category so FR labels and system keys share one cache key
    const productsByCanonicalCategory = new Map();
    sortedProducts.forEach((product) => {
      if (!product?.category) return;
      const categoryKey =
        canonicalizeCategoryKey(product.category, catalogCategories) || product.category;
      if (!productsByCanonicalCategory.has(categoryKey)) {
        productsByCanonicalCategory.set(categoryKey, []);
      }
      productsByCanonicalCategory.get(categoryKey).push(product);
    });

    // Pre-calculate category filters
    productsByCanonicalCategory.forEach((filtered, categoryKey) => {
      const cacheKey = `filter_category_${categoryKey}`;
      this.cache.set(cacheKey, filtered);
      
      // Pre-calculate subcategory combinations for each category
      const categorySubcategories = [...new Set(filtered.map(p => p.subcategory).filter(Boolean))];
      categorySubcategories.forEach(subcategory => {
        const subFiltered = filtered.filter(p => p.subcategory === subcategory);
        
        // For now, use the subcategory as-is (we can add subcategory mapping later if needed)
        const subCacheKey = `filter_category_${categoryKey}_subcategory_${subcategory}`;
        
        this.cache.set(subCacheKey, subFiltered);
        
        // Pre-calculate type combinations for each category+subcategory
        const subTypes = [...new Set(subFiltered.map(p => p.type).filter(Boolean))];
        subTypes.forEach(type => {
          const typeFiltered = subFiltered.filter(p => p.type === type);
          const typeCacheKey = `filter_category_${categoryKey}_subcategory_${subcategory}_type_${type}`;
          this.cache.set(typeCacheKey, typeFiltered);
        });
      });
    });
    
    // Pre-calculate subcategory filters (without category)
    subcategories.forEach(subcategory => {
      const filtered = sortedProducts.filter(p => p.subcategory === subcategory);
      
      // For now, use the subcategory as-is
      const subCacheKey = `filter_subcategory_${subcategory}`;
      
      this.cache.set(subCacheKey, filtered);
    });
    
    // Pre-calculate type filters (without category/subcategory)
    types.forEach(type => {
      const filtered = sortedProducts.filter(p => p.type === type);
      this.cache.set(`filter_type_${type}`, filtered);
    });

    // Search precompute skipped: O(terms×products) froze Inventory/Stock after warm.
    // Live search filters the catalog; category/sub/type indexes above are enough.
  }

  /**
   * Pre-calculate search results for common terms (opt-in; unused on boot warm).
   */
  preCalculateSearchResults(products, { maxTerms = 40 } = {}) {
    const searchTerms = new Set();

    for (const product of products) {
      if (searchTerms.size >= maxTerms) break;
      if (product?.sku) searchTerms.add(String(product.sku).toLowerCase());
      if (product?.name) {
        for (const word of String(product.name).toLowerCase().split(/\s+/)) {
          if (word.length > 2) searchTerms.add(word);
          if (searchTerms.size >= maxTerms) break;
        }
      }
    }

    searchTerms.forEach((term) => {
      const searchResults = products.filter(
        (product) =>
          (product?.name && product.name.toLowerCase().includes(term)) ||
          (product?.sku && product.sku.toLowerCase().includes(term))
      );
      this.cache.set(`search_${term}`, searchResults);
    });
  }

  /**
   * Get cached data instantly
   */
  get(key) {
    return this.cache.get(key);
  }

  /**
   * Set cached data
   */
  set(key, value) {
    this.cache.set(key, value);
  }

  /**
   * Get filtered products instantly using pre-calculated cache
   */
  getFilteredProducts(filters = {}) {
    
    // If no filters, return all products
    if (!filters.search && !filters.category && !filters.subcategory && !filters.type) {
      const allProducts = this.cache.get('filter_') || [];
      return allProducts;
    }
    
    // Try to get exact match from cache first
    const cacheKey = this.buildCacheKey(filters);
    const cachedResult = this.cache.get(cacheKey);
    
    if (cachedResult) {
      return cachedResult;
    }
    
    // If not in cache, try to find the best partial match
    let baseProducts = this.cache.get('filter_') || [];
    
    // Apply category filter if available
    if (filters.category) {
      const canonicalCategory =
        canonicalizeCategoryKey(filters.category, catalogCategories) || filters.category;
      const categoryKey = `filter_category_${canonicalCategory}`;
      // Applying category filter
      const categoryProducts = this.cache.get(categoryKey);
      if (categoryProducts) {
        // Found category products
        baseProducts = categoryProducts;
        
        // Apply subcategory filter if available
        if (filters.subcategory) {
          const subcategoryKey = `filter_category_${canonicalCategory}_subcategory_${filters.subcategory}`;
          // Applying subcategory filter
          const subcategoryProducts = this.cache.get(subcategoryKey);
          if (subcategoryProducts) {
            // Found subcategory products
            baseProducts = subcategoryProducts;
            
            // Apply type filter if available
            if (filters.type) {
              const typeKey = `filter_category_${canonicalCategory}_subcategory_${filters.subcategory}_type_${filters.type}`;
              // Applying type filter
              const typeProducts = this.cache.get(typeKey);
              if (typeProducts) {
                // Found type products
                baseProducts = typeProducts;
              } else {
                // No cached type products found, using live filtering
                baseProducts = baseProducts.filter(p => p.type === filters.type);
              }
            }
          } else {
            // No cached subcategory products found, using live filtering
            baseProducts = baseProducts.filter(p => p.subcategory === filters.subcategory);
          }
        }
      } else {
        // No cached category products found — match by canonical key (keys or FR/AR labels)
        const allProducts = this.cache.get('filter_') || [];
        const frenchCategoryName = getFrenchCategoryName(canonicalCategory);
        baseProducts = allProducts.filter((p) => {
          const productCanon = canonicalizeCategoryKey(p.category, catalogCategories);
          return (
            productCanon === canonicalCategory ||
            p.category === filters.category ||
            p.category === frenchCategoryName
          );
        });
        
        // Apply subcategory filter if available
        if (filters.subcategory) {
          baseProducts = baseProducts.filter(p => p.subcategory === filters.subcategory);
        }
        
        // Apply type filter if available
        if (filters.type) {
          baseProducts = baseProducts.filter(p => p.type === filters.type);
        }
      }
    } else if (filters.subcategory) {
      // Only subcategory filter
      const subcategoryKey = `filter_subcategory_${filters.subcategory}`;
      const subcategoryProducts = this.cache.get(subcategoryKey);
      if (subcategoryProducts) {
        baseProducts = subcategoryProducts;
      }
    } else if (filters.type) {
      // Only type filter
      const typeKey = `filter_type_${filters.type}`;
      const typeProducts = this.cache.get(typeKey);
      if (typeProducts) {
        baseProducts = typeProducts;
      }
    }
    
    // Apply search filter if available
    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      // Applying search filter
      
      const searchKey = `search_${searchTerm}`;
      const searchResults = this.cache.get(searchKey);
      
      if (searchResults) {
        // Found cached search results
        // Intersect search results with filtered products
        const searchSet = new Set(searchResults.map(p => p.id));
        baseProducts = baseProducts.filter(p => searchSet.has(p.id));
        // After intersection
      } else {
        // No cached search results, using live filtering
        // Fallback to live filtering for search
        const beforeFilter = baseProducts.length;
        baseProducts = baseProducts.filter(product => {
        const nameMatch = product?.name && product.name.toLowerCase().includes(searchTerm);
        const skuMatch = product?.sku && product.sku.toLowerCase().includes(searchTerm);
          const matches = nameMatch || skuMatch;
          if (matches) {
            // Product matches
          }
          return matches;
        });
        // Live filtering result
      }
    }
    
    // Cache the result for future use
    this.cache.set(cacheKey, baseProducts);
    
    return baseProducts;
  }

  /**
   * Build cache key for filters
   */
  buildCacheKey(filters) {
    const parts = ['filter'];
    
    if (filters.category) parts.push(`category_${filters.category}`);
    if (filters.subcategory) parts.push(`subcategory_${filters.subcategory}`);
    if (filters.type) parts.push(`type_${filters.type}`);
    if (filters.search) parts.push(`search_${filters.search.toLowerCase()}`);
    
    return parts.join('_');
  }

  /**
   * Get paginated products instantly
   */
  getPaginatedProducts(products, page = 1, pageSize = 50) {
    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return products.slice(startIndex, endIndex);
  }

  /**
   * Clear cache
   */
  clear() {
    this.cache.clear();
    this.filterCache.clear();
    this.isInitialized = false;
    this.lastDataHash = null;
    this.lastFilterHash = null;
    this._posBootHash = null;
    this._deferredHeavyInitScheduled = false;
    this._cachedBusinessId = null;
    this.pendingUpdates = {};
    if (this.updateTimeout) {
      clearTimeout(this.updateTimeout);
      this.updateTimeout = null;
    }
    if (this._deferredHeavyInitRescheduleTimer != null) {
      clearTimeout(this._deferredHeavyInitRescheduleTimer);
      this._deferredHeavyInitRescheduleTimer = null;
    }
  }

  /**
   * Get cache statistics
   */
  getStats() {
    const filterKeys = Array.from(this.cache.keys()).filter(key => key.startsWith('filter_'));
    const searchKeys = Array.from(this.cache.keys()).filter(key => key.startsWith('search_'));
    
    return {
      size: this.cache.size,
      isInitialized: this.isInitialized,
      filterCombinations: filterKeys.length,
      searchTerms: searchKeys.length,
      totalProducts: this.cache.get('productsCount') || 0,
      keys: Array.from(this.cache.keys())
    };
  }

  /**
   * Handle data change events (with error handling)
   */
  handleDataChange(changeEvent) {
    try {
      const { dataType, changeType } = changeEvent;
      
      // Clear relevant cache entries based on data type
      if (dataType === 'products') {
        this.invalidateProductCache();
        this.invalidateProductSalesCache();
        // Force re-initialization with fresh data
        this.initialized = false;
      } else if (dataType === 'suppliers') {
        this.invalidateSupplierCache();
      } else if (dataType === 'lots') {
        this.invalidateLotCache();
      } else if (dataType === 'sales') {
        this.invalidateSalesCache();
        // Force re-initialization with fresh data
        this.initialized = false;
      } else if (dataType === 'customers') {
        this.invalidateCustomerSalesCache();
      }
      
      // Notify that cache needs to be refreshed
      this.notifyCacheUpdate();
    } catch (error) {
      // Error handling data change
      // Don't crash the app, just log the error
    }
  }

  /**
   * Invalidate product-related cache entries
   */
  invalidateProductCache() {
    const keysToDelete = [];
    
    for (const key of this.cache.keys()) {
      if (key.startsWith('filter_') || 
          key.startsWith('search_') || 
          key === 'products' || 
          key === 'productsCount' || 
          key === 'sortedProducts') {
        keysToDelete.push(key);
      }
    }
    
    keysToDelete.forEach(key => this.cache.delete(key));
    
    // Reset cache state
    this.isInitialized = false;
  }

  /**
   * Invalidate supplier-related cache entries
   */
  invalidateSupplierCache() {
    this.cache.delete('suppliers');
  }

  /**
   * Invalidate lot-related cache entries
   */
  invalidateLotCache() {
    this.cache.delete('lots');
  }

  /**
   * Invalidate sales-related cache entries
   */
  invalidateSalesCache() {
    const keysToDelete = [];
    for (const key of this.cache.keys()) {
      if (key.startsWith('filter_sales_') || 
          key.startsWith('search_sales_') || 
          key.startsWith('salesAnalytics') ||
          key.startsWith('topCustomers') ||
          key.startsWith('topProducts') ||
          key.startsWith('monthlyTrends') ||
          key.startsWith('heatmapData') ||
          key.startsWith('salesVelocity') ||
          key.startsWith('profitAnalysis')) {
        keysToDelete.push(key);
      }
    }
    keysToDelete.forEach(key => this.cache.delete(key));
    // Invalidated sales cache
  }

  /**
   * Invalidate customer-related sales cache entries
   */
  invalidateCustomerSalesCache() {
    const keysToDelete = [];
    for (const key of this.cache.keys()) {
      if (key.startsWith('filter_sales_customer_') || 
          key.startsWith('customerSales_') ||
          key.startsWith('topCustomers')) {
        keysToDelete.push(key);
      }
    }
    keysToDelete.forEach(key => this.cache.delete(key));
    // Invalidated customer sales cache
  }

  /**
   * Invalidate product-related sales cache entries
   */
  invalidateProductSalesCache() {
    const keysToDelete = [];
    for (const key of this.cache.keys()) {
      if (key.startsWith('productSales_') ||
          key.startsWith('topProducts') ||
          key.startsWith('profitAnalysis')) {
        keysToDelete.push(key);
      }
    }
    keysToDelete.forEach(key => this.cache.delete(key));
    // Invalidated product sales cache
  }

  /**
   * Pre-calculate all reports data for instant access
   */
  preCalculateReportsData(data) {
    const startTime = performance.now();
    
    const products = data.products || [];
    const sales = data.sales || [];
    const purchases = data.purchases || [];
    const lots = data.lots || {};
    const lotsList = Array.isArray(lots) ? lots : Object.values(lots || {});
    const expiredProducts = mergeExpiredProductsForDisplay(
      data.expiredProducts || [],
      lotsList,
      products
    );
    
    // Pre-calculate stock movement report (ledger preferred for purchased/sold)
    this.preCalculateStockMovementData(products, sales, purchases, lots, {
      stockMovements: data.stockMovements,
      stockMovementsArchive: data.stockMovementsArchive
    });
    
    // Achat vs Vente FIFO is lazy (Sales tab only) — do not block Reports warm
    this.cache.set('achatVsVentePeriods', null);
    this.cache.set('profitAnalysisData', []);
    
    // Pre-calculate expiration alerts
    this.preCalculateExpirationAlerts(data);
    
    // Pre-calculate expired products
    this.preCalculateExpiredProducts(data);
    
    const endTime = performance.now();
  }

  /**
   * Pre-calculate stock *merchandising* report (inStock / coverage / reorder).
   * purchased/sold prefer InventoryMovement aggregates when ledger is present (ADR-0016).
   * For movement timeline use queryInventoryMovementHistory — not this grid.
   */
  preCalculateStockMovementData(products, sales, purchases, lots = {}, options = {}) {
    // Check if we have the required data
    if (!products || products.length === 0) {
      this.cache.set('stockMovementData', []);
      return;
    }

    // Ledger path — authoritative when movements exist
    let ledgerByProduct = null;
    try {
      let movements = options.stockMovements;
      let archive = options.stockMovementsArchive;
      if (!Array.isArray(movements) || movements.length === 0) {
        movements = this.cache.get('stockMovements');
      }
      if ((!Array.isArray(archive) || archive.length === 0) && this.cache.has?.('stockMovementsArchive')) {
        archive = this.cache.get('stockMovementsArchive');
      }
      // Try business blob if warm data omitted movements
      if ((!Array.isArray(movements) || !movements.length)) {
        try {
          const bid = getCurrentBusinessId({ allowFallback: false });
          if (bid) {
            const parsed = readBestBusinessMergedPayload(bid)?.parsed || {};
            movements = parsed.stockMovements;
            archive = parsed.stockMovementsArchive;
          }
        } catch {
          /* optional */
        }
      }
      if (Array.isArray(movements) && movements.length > 0) {
        ledgerByProduct = aggregateMovementProductStats({
          stockMovements: movements,
          stockMovementsArchive: archive
        });
      }
    } catch {
      ledgerByProduct = null;
    }

    // Document path fallback when no ledger (legacy install)
    const purchaseTotals = new Map();
    if (!ledgerByProduct || ledgerByProduct.size === 0) {
      (purchases || []).forEach((purchase) => {
        if (!purchase) return;
        if (
          purchase.productId !== undefined &&
          purchase.productId !== null &&
          purchase.quantity !== undefined &&
          purchase.quantity !== null
        ) {
          const productIdStr = String(purchase.productId);
          const productIdNum = Number(purchase.productId);
          const quantity = Number(purchase.quantity) || 0;
          if (quantity > 0) {
            purchaseTotals.set(productIdStr, (purchaseTotals.get(productIdStr) || 0) + quantity);
            if (!isNaN(productIdNum)) {
              purchaseTotals.set(productIdNum, (purchaseTotals.get(productIdNum) || 0) + quantity);
            }
          }
        } else if (purchase.products && Array.isArray(purchase.products)) {
          purchase.products.forEach((item) => {
            if (!item || item.productId === undefined || item.productId === null) return;
            const productIdStr = String(item.productId);
            const productIdNum = Number(item.productId);
            const quantity = Number(item.quantity) || 0;
            if (quantity > 0) {
              purchaseTotals.set(productIdStr, (purchaseTotals.get(productIdStr) || 0) + quantity);
              if (!isNaN(productIdNum)) {
                purchaseTotals.set(productIdNum, (purchaseTotals.get(productIdNum) || 0) + quantity);
              }
            }
          });
        }
      });
    }

    const salesTotals = new Map();
    if (!ledgerByProduct || ledgerByProduct.size === 0) {
      (sales || []).forEach((sale) => {
        if (sale.products) {
          sale.products.forEach((item) => {
            if (!item || item.productId === undefined || item.productId === null) return;
            const productIdStr = String(item.productId);
            const productIdNum = Number(item.productId);
            const quantity = Number(item.quantity) || 0;
            if (quantity > 0) {
              salesTotals.set(productIdStr, (salesTotals.get(productIdStr) || 0) + quantity);
              if (!isNaN(productIdNum)) {
                salesTotals.set(productIdNum, (salesTotals.get(productIdNum) || 0) + quantity);
              }
            }
          });
        }
      });
    }

    const lotsByProduct = indexLotsByProduct(lots);
    const lotsByProductLocation = indexLotsByProductLocation(lots);
    const hybridVelocityByProduct = buildHybridAverageDailySalesByProduct(sales, 'Principal');
    const todayStartMs = startOfLocalDayMs(new Date());
    const usingLedger = Boolean(ledgerByProduct && ledgerByProduct.size > 0);

    const stockMovementData = products
      .map((product, productIndex) => {
        const productId = String(product.id);
        const productIdNum = Number(product.id);
        const ledger = usingLedger ? getMovementProductStats(ledgerByProduct, product.id) : null;
        const purchased = ledger
          ? ledger.purchased
          : purchaseTotals.get(productId) ||
            purchaseTotals.get(productIdNum) ||
            purchaseTotals.get(String(productIdNum)) ||
            0;
        const sold = ledger
          ? ledger.sold
          : salesTotals.get(productId) ||
            salesTotals.get(productIdNum) ||
            salesTotals.get(String(productIdNum)) ||
            0;

        let stockBreakdown = { Principal: 0 };
        let totalStockAllLocations = 0;
        let stockResolvedFromLots = false;

        try {
          const allLots = lotsByProduct.get(lotProductKey(product.id)) || [];
          stockBreakdown = { Principal: 0 };
          totalStockAllLocations = 0;
          for (let li = 0; li < allLots.length; li += 1) {
            const lot = allLots[li];
            if (!lot || !isLotIncludedInStockMetrics(lot, todayStartMs)) continue;
            const rem = Number(lot.remainingQuantity) || 0;
            if (!(rem > 0)) continue;
            const loc = lot.location || 'Principal';
            stockBreakdown[loc] = (Number(stockBreakdown[loc]) || 0) + rem;
            totalStockAllLocations += rem;
          }
          stockResolvedFromLots = true;
        } catch (e) {
          totalStockAllLocations = Math.max(0, purchased - sold);
          stockBreakdown = { Principal: totalStockAllLocations };
        }

        const inStock = stockResolvedFromLots
          ? totalStockAllLocations
          : product.calculatedQuantity !== undefined
            ? Number(product.calculatedQuantity) || 0
            : Math.max(0, purchased - sold);

        const hybrid = hybridVelocityByProduct.get(productId) || {
          avgDailySales: 0,
          totalSales: 0,
          activeDaysCount: 0,
          isStartupPeriod: true,
          calculationMethod: 'No sales'
        };
        const avgDailySales30Days = hybrid.avgDailySales;
        const isStartupPeriod = hybrid.isStartupPeriod;
        const calculationMethod = hybrid.calculationMethod;

        const principalStock = Number(stockBreakdown['Principal'] || 0);
        const stockInOtherWarehouses = Object.entries(stockBreakdown)
          .filter(([location]) => location !== 'Principal')
          .reduce((sum, [, qty]) => sum + Number(qty || 0), 0);

        const principalCoverage = computePrincipalCoverage(principalStock, avgDailySales30Days);
        const otherCoverage =
          stockInOtherWarehouses > 0 && avgDailySales30Days > 0
            ? stockInOtherWarehouses / avgDailySales30Days
            : 0;

        let conservationType = product.conservation || 'NORMAL';
        try {
          const principalLots =
            lotsByProductLocation.get(lotProductLocationKey(product.id, 'Principal')) || [];
          const lotWithConservation = principalLots.find((lot) => lot.conservationType);
          if (lotWithConservation?.conservationType) {
            conservationType = lotWithConservation.conservationType;
          } else {
            const allLots = lotsByProduct.get(lotProductKey(product.id)) || [];
            const anyConserv = allLots.find((lot) => lot.conservationType);
            if (anyConserv?.conservationType) conservationType = anyConserv.conservationType;
          }
        } catch (e) {
          conservationType = product.conservation || 'NORMAL';
        }

        const { coverageTarget, reorderThreshold, transferThreshold } =
          getCoveragePolicy(conservationType);

        const suggestedReorderQty = computeSuggestedReorderQty(
          avgDailySales30Days,
          principalStock,
          coverageTarget,
          reorderThreshold
        );

        const suggestedTransferQty =
          stockInOtherWarehouses > 0 && principalCoverage < transferThreshold
            ? Math.min(
                stockInOtherWarehouses,
                Math.ceil(avgDailySales30Days * (coverageTarget / 2))
              )
            : 0;

        const unitMargin =
          (product.sellingUnitPrice || 0) - (product.purchaseUnitPrice || 0);
        const velocity = avgDailySales30Days;
        const marginalProfit = velocity * unitMargin;

        return {
          productId: product.id,
          product: product.name,
          purchased,
          sold,
          inStock,
          stockBreakdown,
          qtySource: usingLedger ? 'inventory_ledger' : 'documents',
          avgDailySales30Days: avgDailySales30Days.toFixed(2),
          isStartupPeriod,
          calculationMethod,
          principalStock,
          coverage: {
            principal: principalCoverage > 0 ? Number(principalCoverage.toFixed(1)) : 0,
            other: otherCoverage > 0 ? Number(otherCoverage.toFixed(1)) : 0
          },
          suggestedReorderQty: suggestedReorderQty > 0 ? suggestedReorderQty : '-',
          suggestedTransferQty: suggestedTransferQty > 0 ? suggestedTransferQty : '-',
          hasOtherStocks: stockInOtherWarehouses > 0,
          velocity: velocity.toFixed(2),
          elasticity: '',
          marginalProfit: marginalProfit.toFixed(2),
          statusBadges: [],
          conservationType,
          coverageTarget,
          reorderThreshold,
          transferThreshold,
          updatedAt: product.updatedAt || product.createdAt,
          createdAt: product.createdAt
        };
      })
      .filter((row) => Number(row.inStock) > 0 || Number(row.avgDailySales30Days) > 0);

    stockMovementData.sort((a, b) => {
      const aUpdated = new Date(a.updatedAt || a.createdAt || 0);
      const bUpdated = new Date(b.updatedAt || b.createdAt || 0);
      return bUpdated - aUpdated;
    });

    this.cache.set('stockMovementData', stockMovementData);
  }

  /**
   * Achat vs Vente is computed lazily on the Sales tab (active period only).
   * Keep this as a no-op clearer so callers do not pay FIFO on Reports warm.
   */
  preCalculateProfitAnalysisData() {
    this.cache.set('achatVsVentePeriods', null);
    this.cache.set('profitAnalysisData', []);
  }

  /**
   * Cached Achat vs Vente period bundle (optional; usually null — lazy UI path).
   */
  getAchatVsVentePeriods() {
    return this.cache.get('achatVsVentePeriods') || null;
  }

  /**
   * Pre-calculate expiration alerts
   */
  preCalculateExpirationAlerts(data) {
    // Throttle calls to prevent excessive calculations
    const now = Date.now();
    if (this._lastExpirationCalculation && now - this._lastExpirationCalculation < 2000) {
      // Throttling expiration calculation (too frequent)
      return;
    }
    this._lastExpirationCalculation = now;
    
    try {
      const products = data.products || [];
      const lots = data.lots || {};
      
      // Convert lots to array if needed
      const lotsArray = Array.isArray(lots) ? lots : Object.values(lots || {});
      
      const expirationAlerts = [];
      
      // Check products with lots for expiration alerts (from original Reports page logic)
      products.forEach(product => {
        // Filter lots for this product - check both LOT_STATUS.ACTIVE and 'active' for compatibility
        const productLots = lotsArray.filter(lot => {
          if (!lot || typeof lot !== 'object') return false;
          
          // Match product ID
          const productMatch = String(lot.productId) === String(product.id);
          if (!productMatch) return false;
          
          // Check status - accept both LOT_STATUS.ACTIVE and 'active' (for backward compatibility)
          const status = lot.status || LOT_STATUS.ACTIVE; // Default to ACTIVE if status is missing
          const statusMatch = status === LOT_STATUS.ACTIVE || status === 'active';
          
          // Check remaining quantity
          const hasQuantity = (lot.remainingQuantity || 0) > 0;
          
          return statusMatch && hasQuantity;
        });
        
        productLots.forEach(lot => {
          if (lot.expirationDate) {
            // Conservation thresholds + urgency (ADR-0013 domain/inventory/expiry)
            const conservationType = lot.conservationType || product.conservation || 'PERISSABLE';
            // Sans date products never contribute expiry alerts (even if a lot has a date).
            if (conservationType === 'SANS_DATE' || product.conservation === 'SANS_DATE') {
              return;
            }

            const expirationDate = new Date(lot.expirationDate);
            const today = new Date();
            today.setHours(0, 0, 0, 0); // Normalize to start of day
            const daysUntilExpiry = Math.floor((expirationDate - today) / (1000 * 60 * 60 * 24));

            const alertThreshold = getExpiryAlertThreshold(conservationType) ?? 60;

            // Show ALL products with expiration dates that haven't expired yet
            // The threshold is used for action classification, not filtering
            if (daysUntilExpiry > 0) {
              const action = classifyExpiryUrgency({
                conservation: conservationType,
                daysUntilExpiry
              });
              
              const alert = {
                product: product.name,
                quantity: lot.remainingQuantity || 0,
                expiration: lot.expirationDate,
                daysRemaining: daysUntilExpiry,
                stockLocation: (lot.location || lot.stockLocation || 'Principal'),
                batchNumber: lot.batchNumber || '',
                conservationType: conservationType,
                action: action,
                alertThreshold,
                productId: product.id,
                lotId: lot.id,
                updatedAt: lot.updatedAt || lot.createdAt,
                createdAt: lot.createdAt
              };
              
              expirationAlerts.push(alert);
            }
          }
        });
        
        // Also check products with direct expiration dates (from original Reports page)
        if (product.expirationDate && product.conservation !== 'SANS_DATE') {
          const expirationDate = new Date(product.expirationDate);
          const today = new Date();
          today.setHours(0, 0, 0, 0); // Normalize to start of day
          const daysUntilExpiry = Math.floor((expirationDate - today) / (1000 * 60 * 60 * 24));
          
          const conservationType = product.conservation || 'PERISSABLE';
          const alertThreshold = getExpiryAlertThreshold(conservationType) ?? 60;
          
          // Show ALL products with expiration dates that haven't expired yet
          if (daysUntilExpiry > 0) {
            const action = classifyExpiryUrgency({
              conservation: conservationType,
              daysUntilExpiry
            });
            
            expirationAlerts.push({
              product: product.name,
              quantity: product.quantity || 0,
              expiration: product.expirationDate,
              daysRemaining: daysUntilExpiry,
              stockLocation: 'Principal',
              batchNumber: '',
              conservationType,
              action,
              alertThreshold,
              productId: product.id,
              lotId: '',
              updatedAt: product.updatedAt || product.createdAt,
              createdAt: product.createdAt
            });
          }
        }
      });
      
      // Sort by days remaining (most urgent first)
      expirationAlerts.sort((a, b) => a.daysRemaining - b.daysRemaining);
      
      this.cache.set('expirationAlerts', expirationAlerts);
      
    } catch (error) {
      console.error('❌ [EXPIRATION DEBUG] Error calculating expiration alerts:', error);
      this.cache.set('expirationAlerts', []);
    }
  }

  /**
   * Pre-calculate expired products
   */
  preCalculateExpiredProducts(data) {
    
    try {
      const lotsRaw = data.lots || [];
      const lotsList = Array.isArray(lotsRaw) ? lotsRaw : Object.values(lotsRaw);
      const expiredProducts = mergeExpiredProductsForDisplay(
        data.expiredProducts || [],
        lotsList,
        data.products || []
      );
      
      // Process expired products to ensure they have all required fields (from original Reports page)
      const processedExpiredProducts = expiredProducts.map(expired => ({
        id: expired.id,
        productName: expired.productName || expired.product?.name || '',
        quantity: expired.quantity || 0,
        expirationDate: expired.expirationDate,
        purchaseDate: expired.purchaseDate || expired.createdAt,
        stockLocation: expired.stockLocation || 'Principal',
        status: expired.status || 'Expired',
        productId: expired.productId,
        updatedAt: expired.updatedAt || expired.createdAt,
        createdAt: expired.createdAt
      }));
      
      // Sort by expiration date (most recent first)
      const sortedExpiredProducts = processedExpiredProducts.sort((a, b) => {
        const aDate = new Date(a.expirationDate || 0);
        const bDate = new Date(b.expirationDate || 0);
        return bDate - aDate;
      });
      
      this.cache.set('expiredProducts', sortedExpiredProducts);
    } catch (error) {
      this.cache.set('expiredProducts', []);
    }
  }

  /**
   * Get pre-calculated stock movement data
   */
  getStockMovementData() {
    return this.cache.get('stockMovementData') || [];
  }

  /**
   * Get inventory metrics from cache
   */
  getInventoryMetrics() {
    const data = this.cache.get('inventoryMetrics') || {
      totalValue: 0,
      potentialProfit: 0,
      byLocation: {},
      byCategory: {},
      categories: [],
      subcategories: [],
      types: [],
      totalProducts: 0,
      productsWithStock: 0,
      productsWithoutStock: 0,
      calculationTime: 0
    };
    return data;
  }

  /**
   * Get financial metrics from cache
   */
  getFinancialMetrics() {
    const data = this.cache.get('financialMetrics') || {
      totalRevenue: 0,
      totalCosts: 0,
      totalProfit: 0,
      profitMargin: 0,
      averageOrderValue: 0,
      totalOrders: 0
    };
    return data;
  }

  /**
   * Get time-based metrics from cache
   */
  getTimeBasedMetrics() {
    const data = this.cache.get('timeBasedMetrics') || {
      dailySales: {},
      weeklySales: {},
      monthlySales: {},
      yearlySales: {},
      salesTrend: 'stable',
      growthRate: 0
    };
    return data;
  }

  /**
   * Get category metrics from cache
   */
  getCategoryMetrics() {
    const data = this.cache.get('categoryMetrics') || {
      byCategory: {},
      topCategories: [],
      categoryPerformance: {},
      categoryTrends: {}
    };
    return data;
  }

  /**
   * Get performance metrics from cache
   */
  getPerformanceMetrics() {
    const data = this.cache.get('performanceMetrics') || {
      cacheHitRate: 0,
      averageLoadTime: 0,
      calculationTime: 0,
      memoryUsage: 0,
      lastUpdated: null
    };
    return data;
  }

  /**
   * Get dashboard data from cache
   */
  getDashboardData(type = 'manager') {
    const data = this.cache.get(`dashboardData_${type}`) || {
      summary: {
        totalProducts: 0,
        totalSales: 0,
        totalRevenue: 0,
        totalProfit: 0
      },
      charts: {
        salesTrend: [],
        categoryBreakdown: [],
        topProducts: [],
        recentActivity: []
      },
      alerts: [],
      recommendations: []
    };
    return data;
  }

  /**
   * Get last update time
   */
  getLastUpdateTime() {
    return this.cache.get('lastUpdateTime') || null;
  }

  /**
   * Get pre-calculated expiration alerts
   */
  getExpirationAlerts() {
    const data = this.cache.get('expirationAlerts') || [];
    return data;
  }

  /**
   * Get pre-calculated expired products
   */
  getExpiredProducts() {
    const data = this.cache.get('expiredProducts') || [];
    return data;
  }

  /**
   * Force refresh cache with new data
   */
  refresh(data) {
    this.initialize(data);
  }

  /**
   * Apply partial updates immediately (no debounce) — used for POS boot sync.
   */
  applyPartialDataNow(updates) {
    if (this.updateTimeout) {
      clearTimeout(this.updateTimeout);
      this.updateTimeout = null;
    }
    this.pendingUpdates = { ...this.pendingUpdates, ...updates };
    this._processPendingUpdates();
  }

  /**
   * Debounced update method to prevent excessive calculations
   */
  updatePartialData(updates) {
    // Merge with pending updates
    this.pendingUpdates = { ...this.pendingUpdates, ...updates };
    
    // Clear existing timeout
    if (this.updateTimeout) {
      clearTimeout(this.updateTimeout);
    }
    
    // Set new timeout for debounced update
    this.updateTimeout = setTimeout(() => {
      // Use requestAnimationFrame for better performance
      requestAnimationFrame(() => {
        this._processPendingUpdates();
      });
    }, this.debounceDelay);
  }

  /**
   * Process pending updates after debounce delay
   */
  _processPendingUpdates() {
    if (Object.keys(this.pendingUpdates).length === 0) {
      return;
    }
    
    const updates = { ...this.pendingUpdates };
    this.pendingUpdates = {};
    
    // Keine harte 1s-Drosselung mehr: Updates wurden verworfen → Dashboard/Achats zeigten veralteten Stand.
    this.lastUpdateTime = Date.now();
    
    this._updatePartialDataInternal(updates);
  }

  /**
   * Get current business ID
   */
  _getCurrentBusinessId() {
    try {
      return getCurrentBusinessId({ allowFallback: false }) || null;
    } catch (error) {
      console.error('❌ [INSTANT CACHE] Error getting business ID:', error);
      return null;
    }
  }

  _workerDashboardCacheKey(workerId, businessId = null) {
    const bid = businessId || this._getCurrentBusinessId() || 'unknown_business';
    return `workerDashboard_${bid}_${workerId}`;
  }

  /**
   * Standorte aus Lots ableiten (ohne unifiedDataHelpers zu importieren — vermeidet Zyklen/TDZ beim Bundle-Start).
   */
  _extractLocationsFromLotsPayload(lots, declaredLocations = null) {
    const lotsArray = Array.isArray(lots) ? lots : Object.values(lots || {});
    const lotLocations = [
      ...new Set(
        lotsArray
          .map((lot) => (lot && typeof lot === 'object' ? lot.location : null))
          .filter(Boolean)
      ),
    ];
    const declared = Array.isArray(declaredLocations)
      ? declaredLocations.filter((name) => typeof name === 'string' && name.length > 0)
      : [];
    return [...new Set(['Principal', ...declared, ...lotLocations])];
  }

  /**
   * Locations the business declared in settings — a new location has no lots yet and
   * would otherwise be missing from the stock filter while showing up in transfers.
   */
  _readDeclaredStockLocations() {
    try {
      const businessId = this._getCurrentBusinessId();
      if (!businessId) return [];
      const fields = readBusinessPayloadFieldsSync(businessId);
      return Array.isArray(fields?.stockLocations) ? fields.stockLocations : [];
    } catch (error) {
      return [];
    }
  }

  /**
   * Get stock locations from cache
   */
  getStockLocations() {
    try {
      const cached = this.cache.get('stockLocations');
      if (Array.isArray(cached) && cached.length > 0) {
        return cached;
      }

      const lots = this.cache.get('lots') || {};
      const stockLocations = this._extractLocationsFromLotsPayload(
        lots,
        this._readDeclaredStockLocations()
      );
      this.cache.set('stockLocations', stockLocations);
      return stockLocations;
    } catch (error) {
      console.error('❌ [INSTANT CACHE] Error getting stock locations:', error);
      
      const businessId = this._getCurrentBusinessId();
      if (businessId) {
        try {
          const businessDataKey = businessDataStorageKey(businessId);
          if (!businessDataKey) return ['Principal'];
          const fields = readBusinessPayloadFieldsSync(businessId);
          if (fields.lots?.length) {
            return this._extractLocationsFromLotsPayload(fields.lots);
          }
        } catch (localError) {
          console.error('❌ [INSTANT CACHE] Error getting locations from localStorage:', localError);
        }
      }
      
      return ['Principal'];
    }
  }

  /**
   * Internal method to update specific parts of the cache without overwriting complete data
   * This is safer for partial updates from individual pages
   */
  _updatePartialDataInternal(updates) {
    // Use setTimeout with smaller delay to break up processing
    setTimeout(() => {
      this._processUpdatesChunk(updates);
    }, 5); // Very small delay to break up processing
  }

  _processUpdatesChunk(updates) {
    // Prevent empty data from overwriting good data
    if (updates.products && updates.products.length === 0) {
      delete updates.products;
    }
    
    if (updates.lots && (Array.isArray(updates.lots) ? updates.lots.length === 0 : Object.keys(updates.lots || {}).length === 0)) {
      delete updates.lots;
    }
    
    if (updates.sales && updates.sales.length === 0) {
      delete updates.sales;
    }
    
    if (updates.purchases && updates.purchases.length === 0) {
      delete updates.purchases;
    }

    if (updates.products) {
      const currentProducts = this.cache.get('products') || [];
      if (!this._productsChanged(currentProducts, updates.products)) {
        delete updates.products;
      }
    }

    if (updates.lots) {
      const lotsArray = Array.isArray(updates.lots)
        ? updates.lots
        : Object.values(updates.lots || {});
      const currentLots = this.cache.get('lots') || [];
      if (!this._lotsChanged(currentLots, lotsArray)) {
        delete updates.lots;
      }
    }

    if (Object.keys(updates).length === 0) {
      return;
    }
    
    // Only update the provided data, don't overwrite everything
    if (updates.products) {
      // Check if products actually changed
      const currentProducts = this.cache.get('products') || [];
      const productsChanged = this._productsChanged(currentProducts, updates.products);
      
      if (productsChanged) {
        // Enrich products with stock data before storing
        const lots = updates.lots || this.cache.get('lots') || {};
        const purchases = updates.purchases || this.cache.get('purchases') || [];
        const stockLocations = ['Principal'];
        
        // Use requestAnimationFrame for heavy operations
        requestAnimationFrame(() => {
          const enrichedProducts = this.enrichProductsWithStockData(updates.products, lots, purchases, stockLocations);

          this.cache.set('products', enrichedProducts);
          this.cache.set('productsCount', enrichedProducts.length);

          // Re-calculate filters with new products
          this.preCalculateFilters(enrichedProducts);
          this.notifyCacheUpdate();
        });
      }
    }
    if (updates.suppliers) {
      this.cache.set('suppliers', updates.suppliers);
    }
    if (updates.lots) {
      // FIXED: Ensure lots is always stored as array
      const lotsArray = Array.isArray(updates.lots) ? updates.lots : Object.values(updates.lots || {});
      this.cache.set('lots', lotsArray);

      if (isCoordinatedStockRecalculationActive()) {
        const products = this.cache.get('products') || [];
        const purchases = this.cache.get('purchases') || [];
        const sales = this.cache.get('sales') || [];
        requestAnimationFrame(() => {
          this.preCalculateExpirationAlerts({
            products,
            lots: lotsArray,
            sales,
            purchases
          });
          publishInstantCacheUpdated();
        });
      } else {
      // Re-enrich products with new lots data
      const products = this.cache.get('products') || [];
      const purchases = this.cache.get('purchases') || [];
      const sales = this.cache.get('sales') || [];
      const stockLocations = this.getStockLocations();
      
      if (products.length > 0) {
        // Use requestAnimationFrame for heavy operations
        requestAnimationFrame(() => {
          const enrichedProducts = this.enrichProductsWithStockData(products, lotsArray, purchases, stockLocations);
          this.cache.set('products', enrichedProducts);
          this.cache.set('productsCount', enrichedProducts.length);

          // Re-calculate filters with enriched products
          this.preCalculateFilters(enrichedProducts);
          this.notifyCacheUpdate();
        });
      }
      
      // Recalculate stock rows + inventory metrics when lots change (Achat → Stock → Inventaire)
      requestAnimationFrame(() => {
        this.preCalculateStockData({
          products: this.cache.get('products') || products,
          lots: lotsArray,
          purchases,
          stockLocations
        });
        const inventoryMetrics = this.calculateInventoryMetrics(
          this.cache.get('products') || products,
          lotsArray,
          sales,
          purchases
        );
        this.cache.set('inventoryMetrics', inventoryMetrics);
        const affectedProductIds = [
          ...new Set(lotsArray.map((lot) => lot?.productId).filter(Boolean))
        ];
        this._refreshProductPricingAfterDataChange(lotsArray, purchases, affectedProductIds);
        publishInstantCacheUpdated();
      });
      
      // Recalculate expiration alerts when lots are updated
      // Use requestAnimationFrame for heavy operations
      requestAnimationFrame(() => {
        this.preCalculateExpirationAlerts({
          products: this.cache.get('products') || products,
          lots: lotsArray,
          sales,
          purchases
        });
      });
      }
    }
    if (updates.sales) {
      // Derive KPIs from the passed list, then drop raw history (DataContext SSOT).
      this.cache.set('salesCount', updates.sales.length);
      const customers = updates.customers || this.cache.get('customers') || [];
      const products = this.cache.get('products') || [];
      this.preCalculateSalesData({ sales: updates.sales, customers, products });
      this.cache.delete('sales');
    }
    if (updates.purchases) {
      this.cache.set('purchases', updates.purchases);
      const currentProducts = this.cache.get('products') || [];
      const lots = this.cache.get('lots') || {};
      const sales = this.cache.get('sales') || [];

      requestAnimationFrame(() => {
        const affectedProductIds = [
          ...new Set(
            (updates.purchases || [])
              .flatMap((p) => {
                if (p?.productId != null) return [p.productId];
                return (p?.products || []).map((line) => line.productId).filter(Boolean);
              })
              .filter(Boolean)
          )
        ];
        this._refreshProductPricingAfterDataChange(lots, updates.purchases, affectedProductIds);
      });

      if (currentProducts.length > 0) {
        // Use requestAnimationFrame for heavy operations
        requestAnimationFrame(() => {
          this.preCalculateReportsData({
            products: currentProducts,
            sales: sales,
            purchases: updates.purchases,
            lots: lots,
            expiredProducts: this.cache.get('expiredProducts') || []
          });
        });
      }
    }
    if (updates.customers) {
      this.cache.set('customers', updates.customers);
    }
    if (updates.expiredProducts) {
      // Process expired products to ensure they have all required fields
      this.preCalculateExpiredProducts({
        expiredProducts: updates.expiredProducts
      });
    }
    if (updates.discrepancyManagement) {
      const incoming = updates.discrepancyManagement;
      const incomingCount =
        (incoming.selfDeviations?.length || 0) +
        (incoming.handoverDeviations?.length || 0) +
        (incoming.pendingReviews?.length || 0);
      const existing = this.cache.get('discrepancyManagement');
      const existingCount =
        (existing?.selfDeviations?.length || 0) +
        (existing?.handoverDeviations?.length || 0) +
        (existing?.pendingReviews?.length || 0);
      if (incomingCount > 0 || existingCount === 0) {
        this.cache.set('discrepancyManagement', incoming);
      }
    }
    
    const cachedProducts = this.cache.get('products');
    if (cachedProducts !== undefined) {
      this.isInitialized = true;
    }
    
    // Re-calculate affected data
    if (updates.products || updates.suppliers || updates.lots) {
      this.preCalculateDashboardSpecificData({
        products: updates.products || this.cache.get('products') || [],
        suppliers: updates.suppliers || this.cache.get('suppliers') || [],
        lots: updates.lots || this.cache.get('lots') || {},
        drafts: Array.isArray(updates.drafts)
          ? updates.drafts
          : (this.cache.get('drafts') || []),
        customers: updates.customers || this.cache.get('customers') || [],
        purchases: updates.purchases || this.cache.get('purchases') || [],
      });
    }
    
    // Reports only when explicitly requested — not on every sales/purchases sync.
    if (updates._warmReports && (updates.sales || updates.purchases)) {
      const currentProducts = this.cache.get('products') || [];
      const lots = this.cache.get('lots') || {};
      const purchases = updates.purchases || this.cache.get('purchases') || [];
      
      this.preCalculateReportsData({
        products: currentProducts,
        sales: updates.sales || this.cache.get('sales') || [],
        purchases: purchases,
        lots: lots,
        expiredProducts: this.cache.get('expiredProducts') || []
      });
    }
    
    // Selective dashboard warm (ADR-0014 Phase 3) — never AllMetrics on partial update.
    if (updates.products || updates.sales || updates.purchases || updates.lots || updates.discrepancyManagement) {
      const dashPayload = {
        products: updates.products || this.cache.get('products') || [],
        sales: updates.sales || this.cache.get('sales') || [],
        purchases: updates.purchases || this.cache.get('purchases') || [],
        lots: updates.lots || this.cache.get('lots') || {},
        customers: this.cache.get('customers') || [],
        expenses: this.cache.get('expenses') || [],
        discrepancyManagement: updates.discrepancyManagement || this.cache.get('discrepancyManagement') || { selfDeviations: [], handoverDeviations: [] }
      };

      // Manager: only on explicit warm or écart updates (commits use cacheReadModel.ensureWarm).
      if (updates._warmManager || updates.discrepancyManagement) {
        this.preCalculateManagerDashboard(dashPayload);
      }
      if (updates.lots || updates.products || updates.purchases) {
        this.preCalculateStockData(dashPayload);
        const inventoryMetrics = this.calculateInventoryMetrics(
          dashPayload.products,
          dashPayload.lots,
          dashPayload.sales,
          dashPayload.purchases,
          updates.stockSnapshots ?? this.cache.get('stockSnapshots')
        );
        this.cache.set('inventoryMetrics', inventoryMetrics);
      }
      // Worker/dashboard-specific: only when explicitly requested via updates._warmWorker
      // (POS boot must not pay O(workers×sales) on every sales/lots sync)
      if (updates._warmWorker && (updates.sales || updates.lots)) {
        this.preCalculateWorkerDashboard?.(dashPayload);
        this.preCalculateDashboardSpecificData?.(dashPayload);
      }
    }
    
    // Notify that cache has been updated
    this.notifyCacheUpdate();
    
    // Notify specific card updates
    this.notifyCardUpdates(updates);
  }

  /**
   * Pre-calculate dashboard snapshots.
   * @param {object} data
   * @param {{ includeAllMetrics?: boolean }} [options] AllMetrics only when explicitly requested (boot legacy).
   */
  preCalculateDashboardData(data, options = {}) {
    const includeAllMetrics = options.includeAllMetrics === true;
    
    // Pre-calculate manager dashboard metrics
    this.preCalculateManagerDashboard(data);
    
    // Pre-calculate worker dashboard metrics
    this.preCalculateWorkerDashboard(data);
    
    // Pre-calculate dashboard-specific data (quicksale, cart, etc.)
    this.preCalculateDashboardSpecificData(data);
    
    // Pre-calculate stock data
    this.preCalculateStockData(data);
    
    // Full AllMetrics only on explicit opt-in (ADR-0014 Phase 3 — no default full recalc)
    if (includeAllMetrics) {
      this.preCalculateAllMetrics(data);
    }
  }

  /**
   * Pre-calculate all metrics for instant access
   */
  preCalculateAllMetrics(data) {
    const startTime = performance.now();
    
    const products = data.products || [];
    const sales = data.sales || [];
    const purchases = data.purchases || [];
    const expenses = data.expenses || [];
    const lots = data.lots || {};
    const stockConsumptions = data.stockConsumptions || [];
    const customers = data.customers || [];
    
    // Calculate inventory metrics (ADR-0016 prefer stockSnapshots)
    const inventoryMetrics = this.calculateInventoryMetrics(
      products,
      lots,
      sales,
      purchases,
      data.stockSnapshots
    );
    this.cache.set('inventoryMetrics', inventoryMetrics);
    
    // Calculate financial metrics (including expenses) — frozen COGS first
    const financialMetrics = this.calculateFinancialMetrics(
      sales,
      purchases,
      expenses,
      lots,
      stockConsumptions
    );
    this.cache.set('financialMetrics', financialMetrics);
    
    // Calculate time-based metrics
    const timeBasedMetrics = this.calculateTimeBasedMetrics(sales);
    this.cache.set('timeBasedMetrics', timeBasedMetrics);
    
    // Calculate category metrics
    const categoryMetrics = this.calculateCategoryMetrics(products, sales);
    this.cache.set('categoryMetrics', categoryMetrics);
    
    // Calculate performance metrics
    const performanceMetrics = this.calculatePerformanceMetrics();
    this.cache.set('performanceMetrics', performanceMetrics);
    
    // Calculate dashboard data for different types
    this.cache.set('dashboardData_manager', this.calculateManagerDashboardData(data));
    this.cache.set('dashboardData_worker', this.calculateWorkerDashboardData(data));
    
    // Set last update time
    this.cache.set('lastUpdateTime', new Date().toISOString());
    
    const endTime = performance.now();
  }

  /**
   * ADR-0016: prefer explicit snaps → cache → payload blob.
   * Prevents lot-only overwrite of stock_snapshots totalValue.
   */
  resolveStockSnapshots(stockSnapshots = null, data = null) {
    if (Array.isArray(stockSnapshots) && stockSnapshots.length > 0) {
      this.cache.set('stockSnapshots', stockSnapshots);
      return stockSnapshots;
    }
    if (data?.stockSnapshots) {
      const fromData = Array.isArray(data.stockSnapshots)
        ? data.stockSnapshots
        : Object.values(data.stockSnapshots || {});
      if (fromData.length > 0) {
        this.cache.set('stockSnapshots', fromData);
        return fromData;
      }
    }
    const cached = this.cache.get('stockSnapshots');
    if (Array.isArray(cached) && cached.length > 0) return cached;
    try {
      const bid = getCurrentBusinessId({ allowFallback: false });
      const fromBlob = readStockSnapshotsSyncForBusiness(bid);
      if (Array.isArray(fromBlob) && fromBlob.length > 0) {
        this.cache.set('stockSnapshots', fromBlob);
        return fromBlob;
      }
    } catch {
      /* storage not ready */
    }
    return null;
  }

  /**
   * Calculate inventory metrics
   * ADR-0013 / ADR-0016: valuation owner prefers stockSnapshots
   */
  calculateInventoryMetrics(products, lots, sales, purchases, stockSnapshots = null) {
    try {
      const snaps = this.resolveStockSnapshots(stockSnapshots);
      const result = calculateInventoryValue({ products, lots, stockSnapshots: snaps });
      const lotsByProduct = indexLotsByProduct(lots);
      let productsWithStock = 0;
      let productsWithoutStock = 0;
      for (const p of products) {
        const stockNow = aggregateLotsMetrics(lotsByProduct.get(lotProductKey(p.id)) || []).stockNow;
        if (stockNow > 0) productsWithStock += 1;
        else productsWithoutStock += 1;
      }

      const categories = [...new Set(products.map(p => p.category).filter(Boolean))];
      const subcategories = [...new Set(products.map(p => p.subcategory).filter(Boolean))];
      const types = [...new Set(products.map(p => p.type).filter(Boolean))];
      
      return {
        totalValue: result.totalValue,
        potentialProfit: result.potentialProfit,
        byLocation: result.byLocation,
        byCategory: result.byCategory,
        source: result.source,
        maxLedgerSequence: result.maxLedgerSequence,
        categories,
        subcategories,
        types,
        totalProducts: products.length,
        productsWithStock,
        productsWithoutStock,
        calculationTime: performance.now()
      };
    } catch (error) {
      console.error('❌ [CACHE] Error using CalculationFactory, using fallback:', error);
      // Fallback zu alter Implementierung (sollte nicht mehr benötigt werden)
      return this.calculateInventoryMetricsFallback(products, lots, sales, purchases);
    }
  }
  
  /**
   * Fallback-Berechnung (sollte nicht mehr benötigt werden)
   */
  calculateInventoryMetricsFallback(products, lots, sales, purchases) {
    // Handle both array and object formats for lots
    const lotsArray = Array.isArray(lots) ? lots : Object.values(lots || {});
    
    let totalValue = 0;
    let totalPotentialProfit = 0;
    
    // Group by category
    const byCategory = {};
    const byLocation = {};
    const categories = [...new Set(products.map(p => p.category).filter(Boolean))];
    const subcategories = [...new Set(products.map(p => p.subcategory).filter(Boolean))];
    const types = [...new Set(products.map(p => p.type).filter(Boolean))];
    
    products.forEach(product => {
      // Get lots for this product
      const productLots = lotsArray.filter(lot => {
        if (!lot || typeof lot !== 'object') return false;
        return String(lot.productId) === String(product.id) && isLotIncludedInStockMetrics(lot);
      });
      
      // Calculate product value from lots
      const productValue = productLots.reduce((total, lot) => {
        return total + ((lot.remainingQuantity || 0) * (lot.purchaseUnitPrice || 0));
      }, 0);
      
      // Calculate potential profit from lots
      const productPotentialProfit = productLots.reduce((total, lot) => {
        const sellingPrice = lot.sellingUnitPrice || product.sellingUnitPrice || product.sellingPrice || 0;
        const purchasePrice = lot.purchaseUnitPrice || 0;
        const profitPerUnit = sellingPrice - purchasePrice;
        return total + ((lot.remainingQuantity || 0) * profitPerUnit);
      }, 0);
      
      // Calculate total quantity from lots
      const productQuantity = productLots.reduce((total, lot) => {
        return total + (lot.remainingQuantity || 0);
      }, 0);
      
      totalValue += productValue;
      totalPotentialProfit += productPotentialProfit;
      
      const category = product.category || 'Uncategorized';
      const location = product.location || 'Principal';
      
      if (!byCategory[category]) {
        byCategory[category] = { value: 0, potentialProfit: 0, products: 0 };
      }
      byCategory[category].products++;
      byCategory[category].value += productValue;
      byCategory[category].potentialProfit += productPotentialProfit;
      
      if (!byLocation[location]) {
        byLocation[location] = { value: 0, potentialProfit: 0, products: 0 };
      }
      byLocation[location].products++;
      byLocation[location].value += productValue;
      byLocation[location].potentialProfit += productPotentialProfit;
    });
    
    return {
      totalValue,
      potentialProfit: totalPotentialProfit,
      byLocation,
      byCategory,
      categories,
      subcategories,
      types,
      totalProducts: products.length,
      productsWithStock: products.filter(p => {
        const productLots = lotsArray.filter(lot => {
          if (!lot || typeof lot !== 'object') return false;
          return String(lot.productId) === String(p.id) && isLotIncludedInStockMetrics(lot);
        });
        return productLots.length > 0;
      }).length,
      productsWithoutStock: products.filter(p => {
        const productLots = lotsArray.filter(lot => {
          if (!lot || typeof lot !== 'object') return false;
          return String(lot.productId) === String(p.id) && isLotIncludedInStockMetrics(lot);
        });
        return productLots.length === 0;
      }).length,
      calculationTime: performance.now()
    };
  }

  /**
   * Calculate financial metrics
   * ADR-0013: domain/finance/profit (FIFO)
   */
  calculateFinancialMetrics(sales, purchases, expenses = [], lots = [], stockConsumptions = []) {
    const profit = calculateProfit({ sales, purchases, expenses, lots, stockConsumptions });
    return {
      totalRevenue: profit.revenue,
      totalPurchaseCosts: profit.purchaseCosts,
      totalOperatingExpenses: profit.operatingExpenses,
      totalCosts: profit.totalCosts,
      totalProfit: profit.netProfit,
      profitMargin: profit.profitMargin,
      averageOrderValue: profit.averageOrderValue,
      totalOrders: profit.salesCount,
      costSource: profit.costSource
    };
  }

  /**
   * Calculate time-based metrics
   */
  calculateTimeBasedMetrics(sales) {
    const dailySales = {};
    const weeklySales = {};
    const monthlySales = {};
    const yearlySales = {};
    
    sales.forEach(sale => {
      const date = new Date(sale.createdAt || sale.date || Date.now());
      const day = date.toISOString().split('T')[0];
      const week = this.getWeekKey(date);
      const month = date.toISOString().substring(0, 7);
      const year = date.getFullYear().toString();
      
      const amount = getSaleTotalAmount(sale);
      dailySales[day] = (dailySales[day] || 0) + amount;
      weeklySales[week] = (weeklySales[week] || 0) + amount;
      monthlySales[month] = (monthlySales[month] || 0) + amount;
      yearlySales[year] = (yearlySales[year] || 0) + amount;
    });
    
    return {
      dailySales,
      weeklySales,
      monthlySales,
      yearlySales,
      salesTrend: 'stable', // Could be calculated based on recent trends
      growthRate: 0 // Could be calculated based on period comparison
    };
  }

  /**
   * Calculate category metrics
   */
  calculateCategoryMetrics(products, sales) {
    const byCategory = {};
    const topCategories = [];
    
    // Analyze products by category
    products.forEach(product => {
      const category = product.category || 'Uncategorized';
      if (!byCategory[category]) {
        byCategory[category] = { count: 0, value: 0, revenue: 0 };
      }
      byCategory[category].count++;
      byCategory[category].value += product.value || 0;
    });
    
    // Analyze sales by category
    sales.forEach(sale => {
      if (sale.products) {
        sale.products.forEach(item => {
          const product = products.find(p => p.id === item.productId);
          if (product) {
            const category = product.category || 'Uncategorized';
            if (!byCategory[category]) {
              byCategory[category] = { count: 0, value: 0, revenue: 0 };
            }
            byCategory[category].revenue += item.total || 0;
          }
        });
      }
    });
    
    // Sort categories by revenue
    Object.entries(byCategory).forEach(([category, data]) => {
      topCategories.push({ category, ...data });
    });
    topCategories.sort((a, b) => b.revenue - a.revenue);
    
    return {
      byCategory,
      topCategories: topCategories.slice(0, 10),
      categoryPerformance: {},
      categoryTrends: {}
    };
  }

  /**
   * Calculate performance metrics
   */
  calculatePerformanceMetrics() {
    return {
      cacheHitRate: 0.95, // Could be calculated based on actual hits
      averageLoadTime: 0, // Could be calculated based on actual load times
      calculationTime: performance.now(),
      memoryUsage: 0, // Could be calculated based on actual memory usage
      lastUpdated: new Date().toISOString()
    };
  }

  /**
   * Calculate manager dashboard data
   */
  calculateManagerDashboardData(data) {
    const products = data.products || [];
    const sales = data.sales || [];
    const purchases = data.purchases || [];
    const expenses = data.expenses || [];
    const lots = data.lots || {};
    const stockConsumptions = data.stockConsumptions || [];
    const profit = calculateProfit({ sales, purchases, expenses, lots, stockConsumptions });
    
    return {
      summary: {
        totalProducts: products.length,
        totalSales: sales.length,
        totalRevenue: profit.revenue,
        totalProfit: profit.netProfit
      },
      charts: {
        salesTrend: [],
        categoryBreakdown: [],
        topProducts: [],
        recentActivity: []
      },
      alerts: [],
      recommendations: []
    };
  }

  /**
   * Calculate worker dashboard data
   */
  calculateWorkerDashboardData(data) {
    const products = data.products || [];
    const sales = data.sales || [];
    const { totalRevenue } = summarizeRevenue(sales);
    
    return {
      summary: {
        totalProducts: products.length,
        totalSales: sales.length,
        totalRevenue,
        totalProfit: 0
      },
      charts: {
        salesTrend: [],
        categoryBreakdown: [],
        topProducts: [],
        recentActivity: []
      },
      alerts: [],
      recommendations: []
    };
  }

  /**
   * Get week key for time-based metrics
   */
  getWeekKey(date) {
    const year = date.getFullYear();
    const week = this.getWeekNumber(date);
    return `${year}-W${week.toString().padStart(2, '0')}`;
  }

  /**
   * Get week number
   */
  getWeekNumber(date) {
    const firstDayOfYear = new Date(date.getFullYear(), 0, 1);
    const pastDaysOfYear = (date - firstDayOfYear) / 86400000;
    return Math.ceil((pastDaysOfYear + firstDayOfYear.getDay() + 1) / 7);
  }

  /**
   * Pre-calculate manager dashboard metrics
   */
  preCalculateManagerDashboard(data) {
    
    const sales = data.sales || [];
    const purchases = data.purchases || [];
    const expenses = data.expenses || [];
    const products = data.products || [];
    const lots = data.lots || {};
    const customers = data.customers || [];
    const discrepancyManagement = data.discrepancyManagement || { selfDeviations: [], handoverDeviations: [] };
    const expandedDiscrepancy = expandDiscrepancyManagementForDisplay(discrepancyManagement);
    
    // Date filtering (7 days ago)
    const today = new Date();
    const dateFilter = new Date();
    dateFilter.setDate(dateFilter.getDate() - 7);
    
    const filteredSales = sales.filter(sale => {
      if (!sale || !sale.date) return false;
      const saleDate = new Date(sale.date);
      return saleDate >= dateFilter && !isNaN(saleDate.getTime());
    });
    
    const filteredPurchases = purchases.filter(purchase => {
      if (!purchase || !purchase.date) return false;
      const purchaseDate = new Date(purchase.date);
      return purchaseDate >= dateFilter && !isNaN(purchaseDate.getTime());
    });
    
    const filteredExpenses = expenses.filter(expense => {
      if (!expense || !expense.date) return false;
      const expenseDate = new Date(expense.date);
      return expenseDate >= dateFilter && !isNaN(expenseDate.getTime());
    });

    // ADR-0013: Profit-Owner (FIFO); feste 7-Tage-Fenster für Cache-Snapshot
    const profit = calculateProfit({
      sales: filteredSales,
      purchases: filteredPurchases,
      expenses: filteredExpenses,
      lots
    });
    const totalRevenue = profit.revenue;
    const totalPurchaseCosts = profit.purchaseCosts;
    const totalOperatingExpenses = profit.operatingExpenses;
    const totalCosts = profit.totalCosts;
    const netProfit = profit.netProfit;
    const profitMargin = profit.profitMargin;
    const totalSalesCount = profit.salesCount;
    const averageOrderValue = profit.averageOrderValue;
    
    // Calculate ecart data with resolved worker names
    const selfDeviations = (expandedDiscrepancy.selfDeviations || []).map(deviation => ({
      ...deviation,
      workerName: this.resolveWorkerName(deviation.workerId) || deviation.workerName || ''
    }));
    
    const handoverDeviations = (expandedDiscrepancy.handoverDeviations || []).map(deviation => ({
      ...deviation,
      previousWorkerName: this.resolveWorkerName(deviation.previousWorkerId) || deviation.previousWorkerName || '',
      nextWorkerName: this.resolveWorkerName(deviation.nextWorkerId) || deviation.nextWorkerName || ''
    }));
    
    const allEcarts = [...selfDeviations, ...handoverDeviations];
    const openEcarts = allEcarts.filter(
      (ecart) => isDeviationPending(ecart) && isMeaningfulEcart(ecart)
    );
    
    // FILTER: Nur akzeptierte oder abgelehnte Ecarts für Berechnungen berücksichtigen
    // Ecarts mit Status 'pending' oder 'open' werden NICHT in Rechnungen berücksichtigt
    const resolvedEcarts = allEcarts.filter(ecart => {
      const status = ecart.status || ecart.managerReview?.decision || 'pending';
      return status === 'accepted' || status === 'rejected';
    });
    
    // Calculate positive and negative ecart sums (only for resolved ecarts)
    const positiveEcarts = resolvedEcarts.filter(ecart => {
      const difference = DeviationService.signedDeviationAmount(ecart);
      return Number(difference) > 0;
    });
    
    const negativeEcarts = resolvedEcarts.filter(ecart => {
      const difference = DeviationService.signedDeviationAmount(ecart);
      return Number(difference) < 0;
    });
    
    const positiveEcartSum = positiveEcarts.reduce((sum, ecart) => {
      const difference = DeviationService.signedDeviationAmount(ecart);
      return sum + Math.abs(Number(difference));
    }, 0);
    
    const negativeEcartSum = negativeEcarts.reduce((sum, ecart) => {
      const difference = DeviationService.signedDeviationAmount(ecart);
      return sum + Math.abs(Number(difference));
    }, 0);
    
    // Total is the difference: positive - negative (can be positive or negative)
    const totalEcartSum = positiveEcartSum - negativeEcartSum;
    
    // Calculate positive and negative ecart sums for open ecarts only
    const openPositiveEcarts = openEcarts.filter(ecart => {
      const difference = DeviationService.signedDeviationAmount(ecart);
      return Number(difference) > 0;
    });
    
    const openNegativeEcarts = openEcarts.filter(ecart => {
      const difference = DeviationService.signedDeviationAmount(ecart);
      return Number(difference) < 0;
    });
    
    const openPositiveEcartSum = openPositiveEcarts.reduce((sum, ecart) => {
      const difference = DeviationService.signedDeviationAmount(ecart);
      return sum + Math.abs(Number(difference));
    }, 0);
    
    const openNegativeEcartSum = openNegativeEcarts.reduce((sum, ecart) => {
      const difference = DeviationService.signedDeviationAmount(ecart);
      return sum + Math.abs(Number(difference));
    }, 0);
    
    // Total is the difference: positive - negative (can be positive or negative)
    const openTotalEcartSum = openPositiveEcartSum - openNegativeEcartSum;
    
    
    // ADR-0013: Top Products + Expiry über Domain-Owner (Cache-Snapshot = 7-Tage-Sales)
    const topProducts = rankTopProducts(filteredSales, { limit: 5 });
    const expiringProducts = listExpiringLots({ lots, products, limit: 10 });
    const paymentTotals = this.calculatePaymentTotals(filteredSales);
    
    const managerDashboard = {
      // Financial Metrics (Geld-Fokus)
      totalRevenue,
      totalPurchaseCosts,
      totalOperatingExpenses,
      totalCosts,
      netProfit,
      profitMargin,
      // Sales Metrics
      totalSalesCount,
      averageOrderValue,
      // Payment Totals
      paymentTotals,
      // Other Metrics
      openEcartsCount: openEcarts.length,
      totalEcartsCount: allEcarts.length,
      ecartSums: {
        positive: positiveEcartSum,
        negative: negativeEcartSum,
        total: totalEcartSum,
        positiveCount: positiveEcarts.length,
        negativeCount: negativeEcarts.length
      },
      openEcartSums: {
        positive: openPositiveEcartSum,
        negative: openNegativeEcartSum,
        total: openTotalEcartSum,
        positiveCount: openPositiveEcarts.length,
        negativeCount: openNegativeEcarts.length
      },
      topProducts,
      expiringProducts: expiringProducts.slice(0, 10),
      totalProducts: products.length,
      totalCustomers: customers.length,
      recentSales: filteredSales.slice(0, 5),
      ecartData: {
        selfDeviations,
        handoverDeviations
      },
      allEcarts,
      openEcarts,
      discrepancyManagement: {
        selfDeviations,
        handoverDeviations,
        selfDeviationsCount: selfDeviations.length,
        handoverDeviationsCount: handoverDeviations.length
      }
    };
    
    this.cache.set('managerDashboard', managerDashboard);
  }

  /**
   * Pre-calculate worker dashboard metrics
   */
  preCalculateWorkerDashboard(data) {
    
    const businessId = this._getCurrentBusinessId() || 'unknown_business';
    const sales = data.sales || [];
    const discrepancyManagement = data.discrepancyManagement || { selfDeviations: [], handoverDeviations: [] };
    
    // Get all unique worker IDs from sales
    const workerIds = [...new Set(sales.map(sale => sale.workerId).filter(Boolean))];
    
    workerIds.forEach(workerId => {
      const workerSales = sales.filter(sale => sale.workerId === workerId);
      
      // Get worker sessions from localStorage
      const workerSessions = this.getWorkerSessions(workerId);
      
      // Calculate worker performance metrics
      const performanceMetrics = this.calculateWorkerPerformance(workerSales, workerSessions);
      
      // Filter ecarts for this specific worker
      const workerEcarts = this.getWorkerEcarts(workerId, discrepancyManagement);
      
      const workerDashboard = {
        businessId,
        workerId,
        workerSales,
        workerSessions,
        performanceMetrics,
        workerEcarts, // Add ecarts data for this worker
        assignedTasks: 0, // Will be implemented when task system is added
        completedTasks: 0,
        pendingTasks: 0,
        recentActivities: workerSales.slice(0, 5)
      };
      
      this.cache.set(this._workerDashboardCacheKey(workerId, businessId), workerDashboard);
      // Legacy key kept in sync for backward compatibility
      this.cache.set(`workerDashboard_${workerId}`, workerDashboard);
    });
    
  }

  /**
   * Get ecarts for a specific worker
   */
  getWorkerEcarts(workerId, discrepancyManagement) {
    const selfDeviations = discrepancyManagement.selfDeviations || [];
    const handoverDeviations = discrepancyManagement.handoverDeviations || [];
    
    // Filter self deviations for this worker
    const workerSelfDeviations = selfDeviations.filter(deviation => 
      deviation.workerId === workerId
    );
    
    // Filter handover deviations where this worker is either previous or next worker
    const workerHandoverDeviations = handoverDeviations.filter(deviation => 
      deviation.previousWorkerId === workerId || deviation.nextWorkerId === workerId
    );
    
    // Resolve worker names for better display
    const resolvedSelfDeviations = workerSelfDeviations.map(deviation => ({
      ...deviation,
      workerName: this.resolveWorkerName(deviation.workerId) || deviation.workerName || ''
    }));
    
    const resolvedHandoverDeviations = workerHandoverDeviations.map(deviation => ({
      ...deviation,
      previousWorkerName: this.resolveWorkerName(deviation.previousWorkerId) || deviation.previousWorkerName || '',
      nextWorkerName: this.resolveWorkerName(deviation.nextWorkerId) || deviation.nextWorkerName || ''
    }));
    
    return {
      selfDeviations: resolvedSelfDeviations,
      handoverDeviations: resolvedHandoverDeviations,
      totalCount: resolvedSelfDeviations.length + resolvedHandoverDeviations.length,
      openCount: [...resolvedSelfDeviations, ...resolvedHandoverDeviations].filter(
        deviation => deviation.status === 'open' || deviation.status === 'pending'
      ).length
    };
  }

  /**
   * Resolve worker name from worker ID
   */
  resolveWorkerName(workerId) {
    if (!workerId) {
      return '';
    }
    
    try {
      const allKeys = getStorageEngine().listKvKeys();
      const allSessionKeys = allKeys.filter(
        (key) =>
          key &&
          (key.startsWith('worker_sessions_') ||
            key === 'workerSessions' ||
            key.includes('session'))
      );

      for (const key of allSessionKeys) {
        try {
          const dataVal = kvGet(key);
          const data = Array.isArray(dataVal)
            ? dataVal
            : typeof dataVal === 'string'
              ? JSON.parse(dataVal || '[]')
              : [];
          if (Array.isArray(data)) {
            const session = data.find((s) => s.workerId === workerId);
            if (session) {
              if (session.workerName) {
                return session.workerName;
              }
            }
          }
        } catch {
          /* ignore */
        }
      }

      const business = readStoredCurrentBusiness();
      if (business?.workers && Array.isArray(business.workers)) {
        const worker = business.workers.find((w) => w.id === workerId);
        if (worker && worker.name) {
          return worker.name;
        }
      }

      return '';
    } catch (error) {
      return '';
    }
  }

  /**
   * Get worker sessions from KV
   */
  getWorkerSessions(workerId) {
    const businessId = getCurrentBusinessId();
    if (!businessId || !workerId) return [];

    try {
      return loadSessionsForWorker(businessId, workerId);
    } catch {
      return [];
    }
  }

  /**
   * Calculate worker performance metrics
   */
  calculateWorkerPerformance(workerSales, workerSessions) {
    const totalSales = workerSales.length;
    const { totalRevenue } = summarizeRevenue(workerSales || []);
    
    const averageSaleValue = totalSales > 0 ? totalRevenue / totalSales : 0;
    
    // Calculate session metrics
    const totalSessions = workerSessions.length;
    const completedSessions = workerSessions.filter(session => session.status === 'completed').length;
    
    // Calculate payment method breakdown
    const paymentMethods = {};
    const paymentCategories = { cash: 0, electronic: 0 };
    let totalCash = 0;
    let totalElectronic = 0;
    
    workerSales.forEach(sale => {
      const paymentMethod = sale.paymentMethod || 'cash';
      const { amount, channel } = resolveSaleRegisterIncome(sale);
      
      if (!paymentMethods[paymentMethod]) {
        paymentMethods[paymentMethod] = { amount: 0, count: 0 };
      }
      paymentMethods[paymentMethod].amount += amount;
      paymentMethods[paymentMethod].count += 1;
      
      if (channel === 'cash') {
        totalCash += amount;
        paymentCategories.cash += amount;
      } else if (channel === 'electronic') {
        totalElectronic += amount;
        paymentCategories.electronic += amount;
      }
    });
    
    // Calculate percentages
    const totalAmount = totalCash + totalElectronic;
    const cashPercentage = totalAmount > 0 ? (totalCash / totalAmount) * 100 : 0;
    const electronicPercentage = totalAmount > 0 ? (totalElectronic / totalAmount) * 100 : 0;
    
    // Calculate electronic providers
    const electronicProviders = Object.entries(paymentMethods)
      .filter(([method, data]) => method !== 'cash' && data.count > 0)
      .map(([method, data]) => ({
        name: method,
        totalAmount: data.amount,
        count: data.count
      }))
      .sort((a, b) => b.totalAmount - a.totalAmount);
    
    // Calculate session amounts
    const sessionCount = workerSessions.length;
    const averagePerSession = sessionCount > 0 ? totalAmount / sessionCount : 0;
    
    return {
      totalSales,
      totalRevenue,
      averageSaleValue,
      totalSessions,
      completedSessions,
      completionRate: totalSessions > 0 ? (completedSessions / totalSessions) * 100 : 0,
      // Enhanced metrics for cashier dashboard
      totalCash: Number(totalCash.toFixed(2)),
      totalElectronic: Number(totalElectronic.toFixed(2)),
      totalAmount: Number(totalAmount.toFixed(2)),
      sessionCount,
      averagePerSession: Number(averagePerSession.toFixed(2)),
      cashPercentage: Number(cashPercentage.toFixed(1)),
      electronicPercentage: Number(electronicPercentage.toFixed(1)),
      electronicProviders,
      salesCount: totalSales
    };
  }

  /**
   * Calculate payment totals (simplified version)
   */
  calculatePaymentTotals(filteredSales) {
    const methods = {};
    const categories = {};
    let totalAmount = 0;
    
    filteredSales.forEach(sale => {
      const paymentMethod = sale.paymentMethod || sale.payment_method || 'cash';
      const { amount, channel } = resolveSaleRegisterIncome(sale);
      
      // Store individual payment methods with amount property for dashboard compatibility
      if (!methods[paymentMethod]) {
        methods[paymentMethod] = { amount: 0 };
      }
      methods[paymentMethod].amount += amount;
      
      // Categorize payment methods for dashboard display (credit → no register income)
      if (channel === 'cash' || channel === 'electronic') {
        categories[channel] = (categories[channel] || 0) + amount;
        totalAmount += amount;
      }
    });
    
    // Ensure we have both cash and electronic categories for dashboard
    if (!categories.cash) categories.cash = 0;
    if (!categories.electronic) categories.electronic = 0;
    
    // Ensure we have cash method for dashboard compatibility
    if (!methods.cash) {
      methods.cash = { amount: 0 };
    }
    
    return { methods, categories, totalAmount };
  }

  /**
   * Categorize payment method
   */
  categorizePaymentMethod(method) {
    const key = String(method || '').toLowerCase();
    if (key === 'credit' || key === 'credit_payment' || key === 'on_account') return 'credit';
    const electronicMethods = ['bankily', 'credit_card', 'mobile_payment', 'electronic'];
    return electronicMethods.includes(key) ? 'electronic' : 'cash';
  }

  /**
   * Get manager dashboard data
   */
  getManagerDashboard() {
    return this.cache.get('managerDashboard') || this.getDefaultManagerDashboard();
  }

  /**
   * Get default manager dashboard data
   */
  getDefaultManagerDashboard() {
    return {
      // Financial Metrics
      totalRevenue: 0,
      totalPurchaseCosts: 0,
      totalOperatingExpenses: 0,
      totalCosts: 0,
      netProfit: 0,
      profitMargin: 0,
      // Sales Metrics
      totalSalesCount: 0,
      averageOrderValue: 0,
      // Payment Totals
      paymentTotals: { methods: {}, categories: {}, totalAmount: 0 },
      // Other Metrics
      openEcartsCount: 0,
      totalEcartsCount: 0,
      topProducts: [],
      expiringProducts: [],
      totalProducts: 0,
      totalCustomers: 0,
      recentSales: [],
      ecartData: { selfDeviations: [], handoverDeviations: [] },
      allEcarts: [],
      openEcarts: []
    };
  }

  /**
   * Get worker dashboard data
   */
  getWorkerDashboard(workerId, businessId = null) {
    const currentBusinessId = businessId || this._getCurrentBusinessId() || null;
    const scoped = this.cache.get(this._workerDashboardCacheKey(workerId, currentBusinessId));
    if (scoped) return scoped;

    const legacy = this.cache.get(`workerDashboard_${workerId}`);
    if (legacy && (!currentBusinessId || legacy.businessId === currentBusinessId)) {
      return legacy;
    }
    return this.getDefaultWorkerDashboard();
  }

  /**
   * Get default worker dashboard data
   */
  getDefaultWorkerDashboard() {
    return {
      workerId: null,
      workerSales: [],
      workerSessions: [],
      performanceMetrics: {
        totalSales: 0,
        totalRevenue: 0,
        averageSaleValue: 0,
        totalSessions: 0,
        completedSessions: 0,
        completionRate: 0
      },
      workerEcarts: {
        selfDeviations: [],
        handoverDeviations: [],
        totalCount: 0,
        openCount: 0
      },
      assignedTasks: 0,
      completedTasks: 0,
      pendingTasks: 0,
      recentActivities: []
    };
  }

  /**
   * Get sales analytics from cache
   */
  getSalesAnalytics() {
    return this.cache.get('salesAnalytics') || this.getDefaultSalesAnalytics();
  }

  /**
   * Get default sales analytics data
   */
  getDefaultSalesAnalytics() {
    return {
      totalSales: 0,
      totalRevenue: 0,
      averageOrderValue: 0,
      dailySales: {},
      topCustomers: [],
      topProducts: []
    };
  }

  /**
   * Get filtered sales from cache
   */
  getFilteredSales(filters = {}) {
    const sales = this.cache.get('sales') || [];
    const customers = this.cache.get('customers') || [];
    const cacheKey = this.buildSalesCacheKey(filters);
    const filtered = this.calculateFilteredSales(sales, customers, filters);
    this.cache.set(cacheKey, filtered);
    return filtered;
  }

  /**
   * Build cache key for sales filters
   */
  buildSalesCacheKey(filters) {
    const parts = ['filter_sales'];
    
    if (filters.status && filters.status !== 'all') {
      parts.push(`status_${filters.status}`);
    }
    
    if (filters.customerId) {
      parts.push(`customer_${filters.customerId}`);
    }
    
    if (filters.dateRange) {
      parts.push(`dateRange_${filters.dateRange}`);
    }
    
    if (filters.amountRange) {
      parts.push(`amountRange_${filters.amountRange}`);
    }
    
    if (filters.search) {
      parts.push(`search_${filters.search.toLowerCase()}`);
    }
    
    return parts.join('_');
  }

  /**
   * Calculate filtered sales (fallback when not in cache)
   */
  calculateFilteredSales(sales, customers, filters) {
    let filtered = [...sales];
    
    // Apply status filter
    if (filters.status && filters.status !== 'all') {
      filtered = filtered.filter(sale => sale.status === filters.status);
    }
    
    // Apply customer filter
    if (filters.customerId) {
      filtered = filtered.filter(sale => sale.customerId === filters.customerId);
    }
    
    // Apply date range filter
    if (filters.dateRange) {
      const now = new Date();
      let startDate;
      
      switch (filters.dateRange) {
        case 'today':
          startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
          break;
        case 'week':
          startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          break;
        case 'month':
          startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
          break;
        case 'year':
          startDate = new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000);
          break;
        default:
          startDate = null;
      }
      
      if (startDate) {
        filtered = filtered.filter(sale => new Date(sale.date) >= startDate);
      }
    }
    
    // Apply amount range filter
    if (filters.amountRange) {
      const [min, max] = filters.amountRange.split('-').map(Number);
      filtered = filtered.filter(sale => {
        const amount = getSaleTotalAmount(sale);
        return amount >= min && (max ? amount <= max : true);
      });
    }
    
    // Apply search filter
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(sale => {
        const customer = customers.find(c => c.customerId === sale.customerId);
        return (
          (sale?.orderId && sale.orderId.toLowerCase().includes(searchLower)) ||
          (customer?.name && customer.name.toLowerCase().includes(searchLower)) ||
          (sale?.date && sale.date.includes(searchLower))
        );
      });
    }
    
    return filtered;
  }

  /**
   * Get top customers from cache
   */
  getTopCustomers(limit = 10) {
    const analytics = this.getSalesAnalytics();
    return analytics.topCustomers.slice(0, limit);
  }

  /**
   * Get top products from cache
   */
  getTopProducts(limit = 10) {
    const analytics = this.getSalesAnalytics();
    return analytics.topProducts.slice(0, limit);
  }

  /**
   * Get monthly trends from cache
   */
  getMonthlyTrends() {
    return this.cache.get('monthlyTrends') || [];
  }

  /**
   * Get heatmap data from cache
   */
  getHeatmapData() {
    return this.cache.get('heatmapData') || {};
  }

  /**
   * Get sales velocity from cache
   */
  getSalesVelocity() {
    return this.cache.get('salesVelocity') || 0;
  }

  /**
   * Get profit analysis from cache
   */
  getProfitAnalysis() {
    return this.cache.get('profitAnalysis') || [];
  }

  /**
   * Pre-calculate dashboard-specific data (quicksale, cart, etc.)
   */
  preCalculateDashboardSpecificData(data) {
    
    const products = data.products || [];
    const lots = data.lots || {};
    const customers = data.customers || [];
    
    // Pre-calculate product index for O(1) lookup
    this.preCalculateProductIndex(products);
    
    // Pre-calculate product search data
    this.preCalculateProductSearch(products);

    // inventory_* / lotData_* keys are not prewarmed — getters compute from lots on miss.
    // Lot pricing still warmed for POS checkout (getLotPricingData).
    this.preCalculateLotPricing(products, lots);
    
    // Pre-calculate customer data
    this.preCalculateCustomerData(customers);
    
    // Pre-calculate draft data
    this.preCalculateDraftData(data);
    
    // Pre-calculate product pricing data for product cards
    this.preCalculateProductPricing(products, data.purchases || [], lots);
    
  }

  /**
   * Pre-calculate product index for O(1) lookup
   */
  preCalculateProductIndex(products) {
    const index = new Map();
    products.forEach(product => {
      index.set(String(product.id), product);
    });
    this.cache.set('productIndex', index);
  }

  /**
   * Pre-calculate product search data
   */
  preCalculateProductSearch(products) {
    // Pre-calculate filtered products for common search terms
    const commonSearchTerms = ['', 'live', 'slaughtered', 'feed', 'medicine'];
    const businessTypes = ['general', 'retail'];
    
    businessTypes.forEach(businessType => {
      commonSearchTerms.forEach(searchTerm => {
        const filtered = this.filterProducts(products, searchTerm, { id: businessType });
        const cacheKey = `filteredProducts_${searchTerm}_${businessType}`;
        this.cache.set(cacheKey, filtered);
      });
    });
    
  }

  /**
   * Filter products based on search term and business type
   */
  filterProducts(products, searchTerm, businessType) {
    if (!searchTerm || !searchTerm.trim()) {
      return products;
    }
    
    const searchTermLower = searchTerm.toLowerCase();
    return products.filter(product => {
      // Safe null checks for all string properties
      const matches = (
        (product?.name && product.name.toLowerCase().includes(searchTermLower)) ||
        (product?.sku && product.sku.toLowerCase().includes(searchTermLower)) ||
        (product?.category && product.category.toLowerCase().includes(searchTermLower)) ||
        (product?.subcategory && product.subcategory.toLowerCase().includes(searchTermLower)) ||
        (product?.type && product.type.toLowerCase().includes(searchTermLower))
      );
      
      return matches;
    });
  }

  /**
   * @deprecated No-op. Qty/value come from lots via getAvailableInventory / getProductValue
   * (lazy) and stockData / inventoryMetrics read models. Kept for orchestrator call sites.
   */
  preCalculateInventoryData(_products, _lots) {
    /* intentionally empty — avoids O(products) Principal-only key spam */
  }

  /**
   * Calculate available inventory for a product — lot-only.
   */
  calculateAvailableInventory(productId, stockLocation, lots) {
    return sumAvailableQuantityFromLots(lots, productId, stockLocation);
  }

  /**
   * Calculate product value — lot-only (sellingUnitPrice × remaining).
   */
  calculateProductValue(productId, stockLocation, lots) {
    return sumAvailableValueFromLots(lots, productId, stockLocation);
  }

  /**
   * Calculate stock status
   */
  calculateStockStatus(productId, stockLocation, availableInventory, requestedQty = 0) {
    const available = Number(availableInventory) || 0;
    const requested = Number(requestedQty) || 0;
    const hasInsufficientStock = requested > 0 && requested > available;
    return {
      available,
      hasInsufficientStock,
      message: hasInsufficientStock
        ? `Stock insuffisant (${requested} demandé, ${available} disponible)`
        : ''
    };
  }

  /**
   * Lot pricing warm only (lotData_* had no UI readers).
   */
  preCalculateLotData(products, lots) {
    this.preCalculateLotPricing(products, lots);
  }

  /**
   * Pre-calculate lot pricing data for all products
   */
  preCalculateLotPricing(products, lots) {
    const startTime = performance.now();

    const stockLocations = ['Principal'];
    
    products.forEach(product => {
      stockLocations.forEach(location => {
        const pricingKey = `lotPricing_${product.id}_${location}`;
        const pricingData = this.calculateLotPricing(product, location, lots);
        this.cache.set(pricingKey, pricingData);
      });
    });

    const endTime = performance.now();
  }

  /**
   * Calculate lot pricing for a product
   */
  calculateLotPricing(product, stockLocation, lots) {
    const productLots = normalizeLotsList(lots).filter(
      (lot) =>
        sameProductId(lot.productId, product.id) &&
        lot.location === stockLocation &&
        isLotAvailableForSale(lot)
    );
    
    if (productLots.length === 1) {
      const lot = productLots[0];
      return {
        id: lot.id,
        batchNumber: lot.batchNumber,
        expirationDate: lot.expirationDate,
        remainingQuantity: lot.remainingQuantity,
        purchaseUnitPrice: lot.purchaseUnitPrice,
        sellingUnitPrice: lot.sellingUnitPrice,
        method: 'direct'
      };
    }
    
    return {
      id: null,
      batchNumber: null,
      expirationDate: null,
      remainingQuantity: 0,
      purchaseUnitPrice: 0,
      sellingUnitPrice: product.sellingUnitPrice || 0,
      method: 'fallback'
    };
  }

  /**
   * Calculate lot data for a product
   */
  calculateLotData(product, stockLocation, lots) {
    const productLots = normalizeLotsList(lots).filter(
      (lot) =>
        sameProductId(lot.productId, product.id) &&
        lot.location === stockLocation &&
        isLotAvailableForSale(lot)
    );
    
    if (productLots.length === 1) {
      const lot = productLots[0];
      return {
        id: lot.id,
        batchNumber: lot.batchNumber,
        expirationDate: lot.expirationDate,
        remainingQuantity: lot.remainingQuantity,
        purchaseUnitPrice: lot.purchaseUnitPrice,
        sellingUnitPrice: lot.sellingUnitPrice
      };
    }
    
    return null;
  }

  /**
   * Pre-calculate customer data
   */
  preCalculateCustomerData(customers) {
    // Pre-calculate customer search index
    const customerIndex = new Map();
    customers.forEach(customer => {
      customerIndex.set(String(customer.id), customer);
    });
    this.cache.set('customerIndex', customerIndex);
    
    // Pre-calculate customer search data
    this.preCalculateCustomerSearch(customers);
    
  }

  /**
   * Pre-calculate customer search data
   */
  preCalculateCustomerSearch(customers) {
    const startTime = performance.now();

    // Pre-calculate common customer search terms
    const commonSearchTerms = ['', 'a', 'b', 'c', 'd', 'e', 'f', 'g', 'h', 'i', 'j', 'k', 'l', 'm', 'n', 'o', 'p', 'q', 'r', 's', 't', 'u', 'v', 'w', 'x', 'y', 'z'];
    
    commonSearchTerms.forEach(term => {
      const filtered = this.filterCustomers(customers, term);
      const cacheKey = `customerSearch_${term}`;
      this.cache.set(cacheKey, filtered);
    });

    const endTime = performance.now();
  }

  /**
   * Filter customers based on search term
   */
  filterCustomers(customers, searchTerm) {
    if (!searchTerm || !searchTerm.trim()) return customers.slice(0, 10);
    
    const searchTermLower = searchTerm.toLowerCase();
    return customers
      .filter(customer => {
        const name = (customer?.name || '').toLowerCase();
        const phone = (customer?.phone || '').replace(/\s/g, '');
        const customerId = (customer?.customerId || '').toLowerCase();
        
        return name.includes(searchTermLower) || 
               phone.includes(searchTermLower) || 
               customerId.includes(searchTermLower);
      })
      .slice(0, 10);
  }

  /**
   * Pre-calculate draft data from business payload.
   * Never wipe existing drafts during unrelated recalculations (products/lots/etc.).
   */
  preCalculateDraftData(data) {
    if (Array.isArray(data?.drafts)) {
      this.cache.set('drafts', data.drafts);
      return;
    }
    if (!this.cache.has('drafts')) {
      this.cache.set('drafts', []);
    }
  }

  /**
   * Invalidate product pricing cache (after lot/purchase changes).
   * @param {string|string[]|null} productIds — null = all products
   */
  invalidateProductPricingCache(productIds = null) {
    const idSet =
      productIds == null
        ? null
        : new Set(
            (Array.isArray(productIds) ? productIds : [productIds])
              .filter(Boolean)
              .map(String)
          );

    for (const key of this.cache.keys()) {
      if (key.startsWith('productMostRecentSellingPrice_')) {
        const pid = key.slice('productMostRecentSellingPrice_'.length);
        if (!idSet || idSet.has(pid)) {
          this.cache.delete(key);
        }
      } else if (key.startsWith('productDisplayData_')) {
        if (!idSet) {
          this.cache.delete(key);
        } else {
          for (const pid of idSet) {
            if (key.startsWith(`productDisplayData_${pid}_`)) {
              this.cache.delete(key);
              break;
            }
          }
        }
      }
    }
  }

  _refreshProductPricingAfterDataChange(lots, purchases, productIds = null) {
    const lotsArr = normalizeLotsList(lots ?? this.cache.get('lots'));
    const purchasesList = purchases ?? (this.cache.get('purchases') || []);
    const products = this.cache.get('products') || [];

    if (productIds) {
      this.invalidateProductPricingCache(productIds);
    } else if (lotsArr.length || purchasesList.length) {
      this.invalidateProductPricingCache();
    }

    if (products.length > 0) {
      this.preCalculateProductPricing(products, purchasesList, lotsArr);
    }
  }

  /**
   * Pre-calculate product pricing data for product cards
   */
  preCalculateProductPricing(products, purchases, lots) {
    const stockLocations = ['Principal'];

    const lotsArr = normalizeLotsList(lots);

    products.forEach((product) => {
      const mostRecentSellingPrice = resolveSellingUnitPriceForProduct(
        product.id,
        lotsArr,
        purchases,
        product
      );

      this.cache.set(
        `productMostRecentSellingPrice_${product.id}`,
        mostRecentSellingPrice > 0 ? mostRecentSellingPrice : null
      );

      stockLocations.forEach((location) => {
        const cacheKey = `productDisplayData_${product.id}_${location}`;
        // Live from lots — do not depend on prewarmed inventory_* keys
        const inventory = this.calculateAvailableInventory(product.id, location, lotsArr);

        const productLots = lotsArr.filter(
          (lot) =>
            sameProductId(lot.productId, product.id) &&
            lot.location === location &&
            Number(lot.remainingQuantity) > 0 &&
            Number(lot.sellingUnitPrice) > 0
        );

        let sellingPrice = 0;
        if (productLots.length > 0) {
          const sorted = [...productLots].sort(
            (a, b) =>
              new Date(b.purchaseDate || b.createdAt || 0) -
              new Date(a.purchaseDate || a.createdAt || 0)
          );
          sellingPrice = Number(sorted[0].sellingUnitPrice) || 0;
        } else {
          sellingPrice = mostRecentSellingPrice || product.sellingUnitPrice || 0;
        }

        this.cache.set(cacheKey, {
          inventory,
          sellingPrice,
          mostRecentSellingPrice
        });
      });
    });
  }

  /**
   * Get product index from cache
   */
  getProductIndex() {
    return this.cache.get('productIndex') || new Map();
  }

  /**
   * Available qty — optional cache hit, else lot projection (no Principal-only warm required).
   */
  getAvailableInventory(productId, stockLocation) {
    const cacheKey = `inventory_${productId}_${stockLocation}`;
    if (this.cache.has(cacheKey)) {
      return Number(this.cache.get(cacheKey)) || 0;
    }
    const lots = this.cache.get('lots');
    if (!lots) return 0;
    return this.calculateAvailableInventory(productId, stockLocation, lots);
  }

  /**
   * Drop per-product qty/display keys + stock signature so achat import / lot authority
   * changes cannot leave InstantCache inventing stock for Stock/POS.
   */
  invalidateStockQuantityCache() {
    this._lastStockCalcSignature = null;
    for (const key of this.cache.keys()) {
      if (
        key.startsWith('inventory_') ||
        key.startsWith('productValue_') ||
        key.startsWith('stockStatus_') ||
        key.startsWith('productDisplayData_') ||
        key.startsWith('lotData_') ||
        key.startsWith('lotPricing_')
      ) {
        this.cache.delete(key);
      }
    }
    this.cache.delete('stockData');
    this.cache.delete('purchasedProductIdSet');
  }

  /**
   * Product value — optional cache hit, else lot projection.
   */
  getProductValue(productId, stockLocation) {
    const cacheKey = `productValue_${productId}_${stockLocation}`;
    if (this.cache.has(cacheKey)) {
      return Number(this.cache.get(cacheKey)) || 0;
    }
    const lots = this.cache.get('lots');
    if (!lots) return 0;
    return this.calculateProductValue(productId, stockLocation, lots);
  }

  /**
   * Get customer index from cache
   */
  getCustomerIndex() {
    return this.cache.get('customerIndex') || new Map();
  }

  /**
   * Get customer search results from cache
   */
  getCustomerSearchResults(searchTerm) {
    const cacheKey = `customerSearch_${searchTerm.toLowerCase()}`;
    const cached = this.cache.get(cacheKey);
    if (cached) return cached;
    
    // Fallback to real-time filtering if not cached
    const customers = this.cache.get('customers') || [];
    return this.filterCustomers(customers, searchTerm);
  }

  /**
   * Get lot pricing data from cache
   */
  getLotPricingData(productId, stockLocation) {
    const cacheKey = `lotPricing_${productId}_${stockLocation}`;
    return this.cache.get(cacheKey) || {
      id: null,
      batchNumber: null,
      expirationDate: null,
      remainingQuantity: 0,
      purchaseUnitPrice: 0,
      sellingUnitPrice: 0,
      method: 'fallback'
    };
  }

  /**
   * Get draft data from cache
   */
  getDraftData(businessId) {
    return this.cache.get('drafts') || [];
  }

  /**
   * Get product most recent selling price from cache
   */
  getProductMostRecentSellingPrice(productId) {
    const cacheKey = `productMostRecentSellingPrice_${productId}`;
    const cached = this.cache.get(cacheKey);
    if (cached != null && Number(cached) > 0) return Number(cached);

    const lots = normalizeLotsList(this.cache.get('lots'));
    const purchases = this.cache.get('purchases') || [];
    const products = this.cache.get('products') || [];
    const product = products.find((p) => sameProductId(p.id, productId));
    const resolved = resolveSellingUnitPriceForProduct(
      productId,
      lots,
      purchases,
      product || {}
    );
    if (resolved > 0) {
      this.cache.set(cacheKey, resolved);
    } else {
      this.cache.delete(cacheKey);
    }
    return resolved > 0 ? resolved : null;
  }

  /**
   * Get product display inventory from cache
   */
  getProductDisplayInventory(productId, stockLocation = 'Principal') {
    const cacheKey = `productDisplayData_${productId}_${stockLocation}`;
    const data = this.cache.get(cacheKey);
    return data ? data.inventory : 0;
  }

  /**
   * Get product display selling price from cache
   */
  getProductDisplaySellingPrice(productId, stockLocation = 'Principal') {
    const cacheKey = `productDisplayData_${productId}_${stockLocation}`;
    const data = this.cache.get(cacheKey);
    return data ? data.sellingPrice : 0;
  }

  /**
   * Get complete product display data from cache
   */
  getProductDisplayData(productId, stockLocation = 'Principal') {
    const cacheKey = `productDisplayData_${productId}_${stockLocation}`;
    return this.cache.get(cacheKey) || {
      inventory: 0,
      sellingPrice: 0,
      mostRecentSellingPrice: null
    };
  }

  /**
   * Calculate cart totals
   */
  calculateCartTotals(cart) {
    return cart.reduce((sum, item) => sum + (item.quantity * item.price), 0);
  }

  /**
   * Calculate payment change
   */
  calculatePaymentChange(cartTotal, cashAmount) {
    const cashGiven = Number(cashAmount) || 0;
    const total = Number(cartTotal) || 0;
    return Math.max(0, cashGiven - total);
  }

  /**
   * Enrich products with calculated stock data — O(lots + products).
   */
  enrichProductsWithStockData(products, lots, purchases, stockLocations) {
    const lotsByProduct = indexLotsByProduct(lots);

    return products.map((product) => {
      const productLots = lotsByProduct.get(lotProductKey(product.id)) || [];
      const projected = aggregateLotsMetrics(productLots);
      const stockNow = projected.stockNow;
      const value = projected.value;

      const sellingUnitPrice = resolveSellingUnitPriceForProduct(
        product.id,
        productLots,
        purchases,
        product
      );

      const purchaseUnitPrice = resolvePurchaseUnitPriceForProduct(
        product.id,
        productLots,
        purchases,
        product,
        stockNow,
        value
      );

      return {
        ...product,
        sellingUnitPrice: sellingUnitPrice || product.sellingUnitPrice || product.sellingPrice,
        sellingPrice: sellingUnitPrice || product.sellingPrice || product.sellingUnitPrice,
        purchaseUnitPrice,
        purchasePrice: purchaseUnitPrice,
        calculatedQuantity: stockNow,
        calculatedValue: value,
        calculatedProfit: projected.potentialProfit,
        stockIn: projected.stockIn,
        stockOut: projected.stockOut,
        lotsCount: projected.lotsCount,
        activeLotsCount: projected.activeLotsCount,
        hasLots: productLots.length > 0,
        lastCalculated: new Date().toISOString()
      };
    });
  }

  /**
   * Calculate stock item for a specific product and location.
   * Prefer pre-indexed productLots (O(1) lookup). Optionally overlay snap.current.
   * stockData is projection cache only — not inventory write truth (ADR-0016).
   */
  calculateStockItem(
    product,
    location,
    lots,
    purchases,
    productLotsHint = null,
    snapCurrent = null
  ) {
    const productLots = dedupeLotsList(
      productLotsHint ||
        normalizeLotsList(lots).filter(
          (lot) =>
            sameProductId(lot.productId, product.id) && (!location || lot.location === location)
        )
    );
    const metrics = aggregateLotsMetrics(productLots);
    const hasSnap = snapCurrent != null && Number.isFinite(Number(snapCurrent));
    const stockNow = hasSnap ? Number(snapCurrent) : metrics.stockNow;

    return {
      productId: product.id,
      product: product,
      location: location,
      stockIn: metrics.stockIn,
      stockOut: metrics.stockOut,
      stockNow,
      value: metrics.value,
      potentialProfit: metrics.potentialProfit,
      alertThreshold: resolveLowStockThreshold(product),
      lastActivity:
        productLots.length > 0
          ? productLots[0].updatedAt || productLots[0].createdAt
          : null,
      sortKeyMs: Date.parse(product.updatedAt || product.createdAt || 0) || 0,
      lotsCount: metrics.lotsCount,
      activeLotsCount: metrics.activeLotsCount,
      hasPurchases: false,
      purchaseBased: false,
      projectionSource: hasSnap ? 'stock_snapshot' : 'lots'
    };
  }

  /**
   * Pre-calculate stock table projection cache (not write truth).
   * Prefer snap.current for stockNow when snaps exist; else lot metrics.
   */
  preCalculateStockData(data) {
    const products = data.products || [];
    const lots = data.lots || {};
    // Honour an explicit location list — callers pass one after adding/renaming a location.
    const stockLocations =
      Array.isArray(data.stockLocations) && data.stockLocations.length > 0
        ? data.stockLocations
        : this.getStockLocations();

    // Ghost clones with different ids must not double stockNow (17 → 34)
    const lotsArray = dedupeLotsList(normalizeLotsList(lots));
    const signature = stockProjectionSignature(products, lotsArray, stockLocations);

    if (
      this._lastStockCalcSignature === signature &&
      Array.isArray(this.cache.get('stockData')) &&
      this.cache.get('stockData').length > 0
    ) {
      return;
    }
    this._lastStockCalcSignature = signature;

    const snapCurrentByProductLoc = new Map();
    try {
      let snaps = data.stockSnapshots;
      if (!Array.isArray(snaps) || snaps.length === 0) {
        const bid = getCurrentBusinessId({ allowFallback: false });
        if (bid) snaps = readStockSnapshotsSyncForBusiness(bid);
      }
      if (Array.isArray(snaps)) {
        for (const s of snaps) {
          if (!s || s.productId == null) continue;
          const loc = s.location || 'Principal';
          const key = `${String(s.productId)}|${String(loc)}`;
          snapCurrentByProductLoc.set(
            key,
            (Number(snapCurrentByProductLoc.get(key)) || 0) + (Number(s.current) || 0)
          );
        }
      }
    } catch {
      /* snaps optional */
    }

    const lotsByKey = indexLotsByProductLocation(lotsArray);
    const uniqueItems = [];
    const seen = new Set();

    for (const location of stockLocations) {
      for (const product of products) {
        if (!isStockTrackedCatalogProduct(product)) continue;
        const pid = product?.id;
        if (pid == null) continue;
        const dedupeKey = lotProductLocationKey(pid, location);
        if (seen.has(dedupeKey)) continue;
        seen.add(dedupeKey);
        const productLots = lotsByKey.get(dedupeKey) || [];
        const snapKey = `${String(pid)}|${String(location || 'Principal')}`;
        const snapCur = snapCurrentByProductLoc.has(snapKey)
          ? snapCurrentByProductLoc.get(snapKey)
          : null;
        uniqueItems.push(
          this.calculateStockItem(product, location, lotsArray, null, productLots, snapCur)
        );
      }
    }

    this.cache.set('stockData', uniqueItems);
    this.cache.delete('purchasedProductIdSet');
  }

  /**
   * Pre-calculate sales data
   */
  preCalculateSalesData(data) {
    const sales = data.sales || [];
    const customers = data.customers || [];
    const products = data.products || [];
    const lightAnalytics = data.lightAnalytics !== false;

    // KPI snapshot only — Sales page filters/search live in the page (useMemo).
    // Do NOT rebuild filter_sales_* / search_sales_* here: that was O(customers×sales)
    // and O(terms×sales) with no UI readers, freezing Ventes / Achat-vs-Vente.
    this.preCalculateSalesAnalytics(sales, customers, products, { light: lightAnalytics });
  }

  /**
   * Pre-calculate sales analytics
   * @param {Array} sales
   * @param {Array} customers
   * @param {Array} products
   * @param {{ light?: boolean }} [options] light=true (default): revenue KPIs only
   */
  preCalculateSalesAnalytics(sales, customers, products, options = {}) {
    try {
      const light = options.light !== false;
      // ADR-0013: Revenue Owner — Sales list only needs these three numbers
      const { totalRevenue, salesCount: totalSales, averageOrderValue } = summarizeRevenue(sales);

      if (light) {
        const prev = this.cache.get('salesAnalytics') || {};
        this.cache.set('salesAnalytics', {
          totalSales,
          totalRevenue,
          averageOrderValue,
          dailySales: prev.dailySales || {},
          topCustomers: prev.topCustomers || [],
          topProducts: prev.topProducts || []
        });
        return;
      }

      const dailySales = this.calculateDailySales(sales);
      const topCustomers = this.calculateTopCustomers(sales, customers);
      const topProducts = rankTopProducts(sales, { limit: 10 });

      this.cache.set('salesAnalytics', {
        totalSales,
        totalRevenue,
        averageOrderValue,
        dailySales,
        topCustomers,
        topProducts
      });
    } catch (error) {
      console.error('❌ [INSTANT CACHE] Error pre-calculating sales analytics:', error);
      this.cache.set('salesAnalytics', this.getDefaultSalesAnalytics());
    }
  }

  /**
   * @deprecated Sales list filters live in Sales.jsx — kept as no-op for callers.
   */
  preCalculateSalesFilters() {
    /* intentionally empty — was O(customers × sales) with no UI readers */
  }

  /**
   * @deprecated Sales search lives in Sales.jsx — kept as no-op for callers.
   */
  preCalculateSalesSearch() {
    /* intentionally empty — was O(terms × sales) with no UI readers */
  }

  /**
   * @deprecated Advanced sales charts unused by Sales page — no-op to avoid
   * all-history FIFO profitAnalysis on every sales warm.
   */
  preCalculateAdvancedSalesAnalytics() {
    /* intentionally empty */
  }

  /**
   * Calculate daily sales for last 30 days
   */
  calculateDailySales(sales) {
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    
    const recentSales = sales.filter(sale => 
      new Date(sale.date) >= thirtyDaysAgo
    );
    
    const dailySales = {};
    recentSales.forEach(sale => {
      const date = sale.date;
      if (!dailySales[date]) {
        dailySales[date] = { count: 0, revenue: 0 };
      }
      dailySales[date].count += 1;
      dailySales[date].revenue += getSaleTotalAmount(sale);
    });
    
    return dailySales;
  }

  /**
   * Calculate top customers
   */
  calculateTopCustomers(sales, customers) {
    const customerById = new Map();
    for (let i = 0; i < (customers || []).length; i += 1) {
      const c = customers[i];
      if (c?.customerId != null) customerById.set(String(c.customerId), c);
    }
    const customerSales = {};
    for (let i = 0; i < (sales || []).length; i += 1) {
      const sale = sales[i];
      if (!sale?.customerId) continue;
      const customer = customerById.get(String(sale.customerId));
      const customerName = customer ? customer.name : '';
      if (!customerSales[customerName]) {
        customerSales[customerName] = { count: 0, revenue: 0 };
      }
      customerSales[customerName].count += 1;
      customerSales[customerName].revenue += getSaleTotalAmount(sale);
    }
    
    return Object.entries(customerSales)
      .map(([name, data]) => ({ name, ...data }))
      .sort((a, b) => b.revenue - a.revenue)
      .slice(0, 10);
  }

  /**
   * Calculate monthly trends
   */
  calculateMonthlyTrends(sales) {
    const monthlySales = {};
    sales.forEach(sale => {
      const month = sale.date.substring(0, 7); // YYYY-MM
      if (!monthlySales[month]) {
        monthlySales[month] = { count: 0, revenue: 0 };
      }
      monthlySales[month].count += 1;
      monthlySales[month].revenue += getSaleTotalAmount(sale);
    });
    
    return Object.entries(monthlySales)
      .map(([month, data]) => ({ month, ...data }))
      .sort((a, b) => a.month.localeCompare(b.month))
      .slice(-12); // Last 12 months
  }

  /**
   * Calculate sales velocity
   */
  calculateSalesVelocity(sales) {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(endDate.getDate() - 14); // Last 14 days
    
    const recentSales = sales.filter(sale => {
      const saleDate = new Date(sale.date);
      return saleDate >= startDate && saleDate <= endDate;
    });
    
    return recentSales.length / 14; // Sales per day
  }

  /**
   * Profit analysis warm cache — Adapter über domain/finance/profit (PR7).
   * Portfolio totals: calculateProfit (SSOT). Per-product rows use sale-line
   * purchaseUnitPrice/usedLots for display only (not a second P&L owner).
   * Manager dashboard KPIs stay on DFM — do not route InstantCache → DFM.
   */
  calculateProfitAnalysis(sales, products, lots = {}) {
    const portfolio = calculateProfit({
      sales: sales || [],
      purchases: [],
      expenses: [],
      lots
    });

    const productProfits = {};

    (sales || []).forEach((sale) => {
      if (!sale.products) return;
      sale.products.forEach((product) => {
        const productId = product.productId || product.id;
        if (!productProfits[productId]) {
          productProfits[productId] = {
            totalRevenue: 0,
            totalCost: 0,
            quantity: 0,
            salesCount: 0
          };
        }

        const quantity = Number(product.quantity) || 0;
        const sellingPrice = Number(product.price || product.sellingUnitPrice) || 0;
        let unitCost = Number(product.purchaseUnitPrice) || 0;
        if (!(unitCost > 0) && Array.isArray(product.usedLots) && product.usedLots.length > 0) {
          const lotCost = product.usedLots.reduce(
            (sum, lot) => sum + (Number(lot.unitPrice) || 0) * (Number(lot.quantity) || 0),
            0
          );
          unitCost = quantity > 0 ? lotCost / quantity : 0;
        }

        productProfits[productId].totalRevenue += quantity * sellingPrice;
        productProfits[productId].totalCost += quantity * unitCost;
        productProfits[productId].quantity += quantity;
        productProfits[productId].salesCount += 1;
      });
    });

    const rows = Object.entries(productProfits)
      .map(([productId, data]) => {
        const profit = data.totalRevenue - data.totalCost;
        const profitMargin =
          data.totalRevenue > 0 ? (profit / data.totalRevenue) * 100 : 0;
        return {
          productId,
          ...data,
          profit,
          profitMargin
        };
      })
      .sort((a, b) => b.profit - a.profit);

    rows.portfolio = {
      revenue: portfolio.revenue,
      purchaseCosts: portfolio.purchaseCosts,
      netProfit: portfolio.netProfit,
      profitMargin: portfolio.profitMargin,
      costSource: portfolio.costSource
    };

    return rows;
  }

  /**
   * Calculate heatmap data
   */
  calculateHeatmapData(sales) {
    const heatmapData = {};
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    
    const recentSales = sales.filter(sale => 
      new Date(sale.date) >= thirtyDaysAgo
    );
    
    recentSales.forEach(sale => {
      const date = sale.date;
      if (!heatmapData[date]) {
        heatmapData[date] = { count: 0, revenue: 0 };
      }
      heatmapData[date].count += 1;
      heatmapData[date].revenue += getSaleTotalAmount(sale);
    });
    
    return heatmapData;
  }

  /**
   * Pre-calculate date range filters
   */
  preCalculateDateRangeFilters(sales) {
    const now = new Date();
    const dateRanges = [
      { key: 'today', start: new Date(now.getFullYear(), now.getMonth(), now.getDate()) },
      { key: 'week', start: new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000) },
      { key: 'month', start: new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000) },
      { key: 'year', start: new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000) }
    ];
    
    dateRanges.forEach(range => {
      const filtered = sales.filter(sale => new Date(sale.date) >= range.start);
      this.cache.set(`filter_sales_dateRange_${range.key}`, filtered);
    });
  }

  /**
   * Pre-calculate amount range filters
   */
  preCalculateAmountRangeFilters(sales) {
    const amountRanges = [
      { key: '0-100', min: 0, max: 100 },
      { key: '100-500', min: 100, max: 500 },
      { key: '500-1000', min: 500, max: 1000 },
      { key: '1000+', min: 1000, max: Infinity }
    ];
    
    amountRanges.forEach(range => {
      const filtered = sales.filter(sale => {
        const amount = getSaleTotalAmount(sale);
        return amount >= range.min && amount <= range.max;
      });
      this.cache.set(`filter_sales_amountRange_${range.key}`, filtered);
    });
  }

  /**
   * Force refresh stock data cache
   */
  refreshStockDataCache() {
    const businessId = getCurrentBusinessId();
    if (!businessId) {
      console.warn('⚠️ [INSTANT CACHE] No business ID found for refresh');
      return;
    }

    if (!businessDataStorageKey(businessId)) return;
    const fields = readBusinessPayloadFieldsSync(businessId);
    this.preCalculateStockData({
      products: fields.products || [],
      lots: fields.lots || [],
      purchases: fields.purchases || [],
      stockLocations: this.getStockLocations() || ['Principal']
    });
  }

  /**
   * True when stock rows need ensureWarm — does not recalculate.
   * Do NOT treat "all stockNow === 0" as stale: that is a valid state and
   * caused an endless ensureWarm → STOCK_CALC loop (UI vibration).
   */
  isStockProjectionStale() {
    const stockData = this.cache.get('stockData') || [];
    const products = this.cache.get('products') || [];
    if (stockData.length === 0) {
      return Array.isArray(products) && products.length > 0;
    }

    const cachedLocs = this.cache.get('stockLocations');
    const currentStockLocations =
      Array.isArray(cachedLocs) && cachedLocs.length > 0
        ? cachedLocs
        : this.getStockLocations();
    const existingLocations = [...new Set(stockData.map((item) => item.location))];
    const missingLocations = currentStockLocations.filter(
      (loc) => !existingLocations.includes(loc)
    );
    return missingLocations.length > 0;
  }

  /**
   * Product ids that belong in stock (lots ∪ purchases) — O(lots+lines), cached.
   */
  _getPurchasedProductIdSet() {
    const cached = this.cache.get('purchasedProductIdSet');
    if (cached instanceof Set) return cached;

    const ids = new Set();
    const lotsInCache = this.cache.get('lots') || {};
    const lotsArray = Array.isArray(lotsInCache)
      ? lotsInCache
      : Object.values(lotsInCache || {});
    for (const lot of lotsArray) {
      if (lot?.productId != null) ids.add(String(lot.productId));
    }

    try {
      const purchases = this.cache.get('purchases') || [];
      const { flattenPurchaseLineItems } = require('../utils/lotSystem');
      const lines = flattenPurchaseLineItems(purchases);
      for (const line of lines) {
        if (line?.productId != null) ids.add(String(line.productId));
      }
    } catch {
      /* optional */
    }

    this.cache.set('purchasedProductIdSet', ids);
    return ids;
  }

  /**
   * Pure read + filter over cached stockData. Never calls preCalculateStockData.
   */
  getFilteredStockData(filters = {}) {
    let dataToFilter = this.cache.get('stockData') || [];
    // Defense: plats menu never belong on Stock (even if stale stockData rows remain).
    dataToFilter = dataToFilter.filter((item) =>
      isStockTrackedCatalogProduct(item.product || item)
    );
    const purchasedIds = this._getPurchasedProductIdSet();

    if (purchasedIds.size > 0) {
      dataToFilter = dataToFilter.filter((item) =>
        purchasedIds.has(String(item.productId))
      );
    }

    // One status key: the UI writes `stockFilter`, older callers `stockStatus`.
    const stockFilter = filters.stockFilter || filters.stockStatus || 'all';
    const hideZeroStock = filters.showZeroStock === false;
    // 'all' means unfiltered — the select emits it as a real value.
    const locationFilter =
      filters.location && filters.location !== 'all' ? filters.location : '';

    if (stockFilter === 'lowStock') {
      dataToFilter = dataToFilter.filter((item) => {
        const stock = item.stockNow || 0;
        if (stock <= 0) return false;
        return stock <= resolveLowStockThreshold(item.product || item);
      });
    } else if (hideZeroStock || stockFilter === 'inStock') {
      dataToFilter = dataToFilter.filter((item) => (item.stockNow || 0) > 0);
    } else if (stockFilter === 'outOfStock') {
      dataToFilter = dataToFilter.filter((item) => (item.stockNow || 0) === 0);
    }

    let filtered = dataToFilter.filter((item) => {
      const product = item.product || item;

      if (filters.search) {
        const searchTerm = String(filters.search).toLowerCase();
        const matchesSearch =
          (product?.name && product.name.toLowerCase().includes(searchTerm)) ||
          (product?.barcode && product.barcode.toLowerCase().includes(searchTerm)) ||
          (product?.description && product.description.toLowerCase().includes(searchTerm));
        if (!matchesSearch) return false;
      }

      if (filters.category && product.category !== filters.category) return false;
      if (filters.subcategory && product.subcategory !== filters.subcategory) return false;
      if (filters.type && product.type !== filters.type) return false;
      if (locationFilter && item.location !== locationFilter) return false;

      return true;
    });

    // SKU descending (numeric-aware) — same order as inventaire table
    filtered.sort((a, b) => {
      const aSku = String((a.product || a).sku || a.sku || '');
      const bSku = String((b.product || b).sku || b.sku || '');
      return bSku.localeCompare(aSku, undefined, { numeric: true, sensitivity: 'base' });
    });

    return filtered;
  }

  /**
   * Get visible stock products (only purchased products)
   */
  getVisibleStockProducts() {
    const products = this.cache.get('products') || [];
    const lots = this.cache.get('lots') || {};
    const purchases = this.cache.get('purchases') || [];

    try {
      return getVisibleProducts(products, lots, purchases, 'purchased');
    } catch (error) {
      // Fallback: purchased products from stock rows (including zero stock)
      const stockData = this.cache.get('stockData') || [];
      return stockData
        .map((item) => item.product)
        .filter(Boolean)
        .filter((product, index, self) =>
          index === self.findIndex((p) => p.id === product.id)
        );
    }
  }


  /**
   * Stock timeline is ledger-owned (InventoryMovementHistoryPanel / loadInventoryMovementHistory).
   * Legacy lots+purchases+sales composite removed (ADR-0016).
   */

  /**
   * Get stock data for a specific product and location
   */
  getStockForProduct(productId, location) {
    const stockData = this.cache.get('stockData') || [];
    return stockData.find(item => 
      sameProductId(item.productId, productId) && 
      (location ? item.location === location : true)
    );
  }

  /**
   * Get all stock for a specific product across all locations
   */
  getStockForProductAllLocations(productId) {
    const stockData = this.cache.get('stockData') || [];
    return stockData.filter(item => item.productId === productId);
  }

  /**
   * Get stock for a specific location
   */
  getStockForLocation(location) {
    const stockData = this.cache.get('stockData') || [];
    return stockData.filter(item => item.location === location);
  }

  /**
   * Calculate total stock for a product across all locations
   */
  getTotalStockForProduct(productId) {
    const productStocks = this.getStockForProductAllLocations(productId);
    return productStocks.reduce((total, item) => total + item.stockNow, 0);
  }

  /**
   * Pre-calculate purchases data
   */
  preCalculatePurchasesData(data) {
    const startTime = performance.now();
    
    const rawPurchases = data.purchases || [];
    // Soft-voided lines stay in storage but KPIs / list groups use active rows only
    const purchases = rawPurchases.filter(isActivePurchaseRow);
    const suppliers = data.suppliers || [];
    const products = data.products || [];
    

    
    // Pre-calculate grouped purchases
    this.preCalculateGroupedPurchases(purchases);
    
    // Pre-calculate purchases analytics
    this.preCalculatePurchasesAnalytics(purchases, suppliers, products);
    
    // Pre-calculate purchases filters
    this.preCalculatePurchasesFilters(purchases, suppliers);
    
    // Pre-calculate purchases search indexes
    this.preCalculatePurchasesSearchIndexes(purchases, suppliers);
    
    const endTime = performance.now();

  }

  /**
   * Pre-calculate grouped purchases
   */
  preCalculateGroupedPurchases(purchases) {
    try {
      // Kopie sortieren — niemals das React-State-Array mutieren (sonst leere/falsche UI)
      const sortedPurchases = [...purchases].sort((a, b) => {
        const safeDate = (row) => {
          const d = row?.date;
          if (!d) return 0;
          const t = new Date(String(d).includes('T') ? d : `${d}T${row?.time || '00:00:00'}`);
          const ms = t.getTime();
          return Number.isNaN(ms) ? 0 : ms;
        };
        const timestampA = safeDate(a);
        const timestampB = safeDate(b);
        if (timestampA === timestampB) {
          return (Number(b?.id) || 0) - (Number(a?.id) || 0);
        }
        return timestampB - timestampA;
      });

      // Store sorted purchases
      this.cache.set('sortedPurchases', sortedPurchases);

      // Group by purchaseId
      const grouped = sortedPurchases.reduce((groups, purchase) => {
        const purchaseId = purchase.purchaseId;
        if (!groups[purchaseId]) {
          groups[purchaseId] = [];
        }
        groups[purchaseId].push(purchase);
        return groups;
      }, {});

      // Convert to array format for display
      const purchaseGroups = Object.entries(grouped)
        .filter(([purchaseId]) => !purchaseId.startsWith('TRANSFER_'))
        .filter(([, purchaseList]) => !isPurchaseReceiptFullyVoided(purchaseList))
        .map(([purchaseId, purchaseList]) => {
          const firstPurchase = purchaseList[0];
          const totalAmount = purchaseList.reduce((sum, p) => sum + p.total, 0);
          const totalItems = purchaseList.reduce((sum, p) => sum + p.quantity, 0);
          
          return {
            purchaseId,
            date: firstPurchase.date,
            supplierId: firstPurchase.supplierId,
            deliveryDate: firstPurchase.deliveryDate,
            total: totalAmount,
            totalItems,
            purchases: purchaseList
          };
        })
        .sort((a, b) =>
          String(b.purchaseId || '').localeCompare(String(a.purchaseId || ''))
        );

      // Store grouped purchases
      this.cache.set('groupedPurchases', purchaseGroups);
      this.cache.set('sortedGroupedPurchases', purchaseGroups);


    } catch (error) {
      console.error('❌ [INSTANT CACHE] Error pre-calculating grouped purchases:', error);
      this.cache.set('groupedPurchases', []);
      this.cache.set('sortedGroupedPurchases', []);
    }
  }

  /**
   * Pre-calculate purchases analytics
   */
  preCalculatePurchasesAnalytics(purchases, suppliers, products) {
    try {
      const totalPurchases = purchases.length;
      const totalAmount = purchases.reduce((sum, p) => sum + (p.total || 0), 0);
      const averageValue = totalPurchases > 0 ? totalAmount / totalPurchases : 0;
      const totalItems = purchases.reduce((sum, p) => sum + (p.quantity || 0), 0);
      const averageItemsPerPurchase = totalPurchases > 0 ? totalItems / totalPurchases : 0;

      // Calculate top suppliers
      const topSuppliers = this.calculateTopSuppliers(purchases, suppliers);
      
      // Calculate top products
      const topProducts = this.calculateTopPurchaseProducts(purchases, products);
      
      // Calculate monthly trends
      const monthlyTrends = this.calculateMonthlyPurchasesTrends(purchases);
      
      // Calculate supplier analytics
      const supplierAnalytics = this.calculateSupplierAnalytics(purchases, suppliers);
      
      // Calculate product analytics
      const productAnalytics = this.calculateProductAnalytics(purchases, products);

      // Store analytics
      this.cache.set('purchasesAnalytics', {
        totalPurchases,
        totalAmount,
        averageValue,
        totalItems,
        averageItemsPerPurchase,
        topSuppliers,
        topProducts,
        monthlyTrends,
        supplierAnalytics,
        productAnalytics
      });

      // Store individual metrics
      this.cache.set('totalPurchasesAmount', totalAmount);
      this.cache.set('averagePurchaseValue', averageValue);


    } catch (error) {
      console.error('❌ [INSTANT CACHE] Error pre-calculating purchases analytics:', error);
      this.cache.set('purchasesAnalytics', this.getDefaultPurchasesAnalytics());
    }
  }

  /**
   * Pre-calculate purchases filters
   */
  preCalculatePurchasesFilters(purchases, suppliers) {
    try {
      // All purchases (no filters)
      this.cache.set('filter_purchases_', purchases);
      
      // Date-based filters
      const today = new Date().toISOString().split('T')[0];
      const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      const monthAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      const yearAgo = new Date(Date.now() - 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      
      this.cache.set('filter_purchases_today', 
        purchases.filter(p => p.date === today));
      this.cache.set('filter_purchases_week', 
        purchases.filter(p => p.date >= weekAgo));
      this.cache.set('filter_purchases_month', 
        purchases.filter(p => p.date >= monthAgo));
      this.cache.set('filter_purchases_year', 
        purchases.filter(p => p.date >= yearAgo));
      
      // Supplier filters
      suppliers.forEach(supplier => {
        const supplierPurchases = purchases.filter(p => p.supplierId === supplier.supplierId);
        this.cache.set(`filter_purchases_supplier_${supplier.supplierId}`, supplierPurchases);
      });
      
      // Product filters
      const productIds = [...new Set(purchases.map(p => p.productId))];
      productIds.forEach(productId => {
        const productPurchases = purchases.filter(p => p.productId === productId);
        this.cache.set(`filter_purchases_product_${productId}`, productPurchases);
      });


    } catch (error) {
      console.error('❌ [INSTANT CACHE] Error pre-calculating purchases filters:', error);
    }
  }

  /**
   * Pre-calculate purchases search indexes
   */
  preCalculatePurchasesSearchIndexes(purchases, suppliers) {
    try {
      // Validate inputs
      if (!purchases || !Array.isArray(purchases)) {
        this.cache.set('search_purchases_receipt_index', new Map());
        this.cache.set('search_purchases_supplier_index', new Map());
        this.cache.set('search_purchases_combined_index', new Map());
        return;
      }

      // Receipt number search index
      const receiptIndex = new Map();
      purchases.forEach(purchase => {
        if (!purchase || typeof purchase !== 'object') return;
        
        // Ensure purchaseId is converted to string before toLowerCase
        const purchaseId = String(purchase?.purchaseId || purchase?.id || '').toLowerCase();
        if (purchaseId) {
          if (!receiptIndex.has(purchaseId)) {
            receiptIndex.set(purchaseId, []);
          }
          receiptIndex.get(purchaseId).push(purchase);
        }
      });
      this.cache.set('search_purchases_receipt_index', receiptIndex);
      
      // Supplier name search index
      const supplierIndex = new Map();
      if (suppliers && Array.isArray(suppliers)) {
        suppliers.forEach(supplier => {
          if (!supplier || typeof supplier !== 'object') return;
          
          // Ensure name is converted to string before toLowerCase
          const name = String(supplier?.name || supplier?.supplierName || '').toLowerCase();
          if (name) {
            const supplierPurchases = purchases.filter(p => 
              p && (p.supplierId === supplier.supplierId || p.supplierId === supplier.id)
            );
            if (supplierPurchases.length > 0) {
              supplierIndex.set(name, supplierPurchases);
            }
          }
        });
      }
      this.cache.set('search_purchases_supplier_index', supplierIndex);
      
      // Combined search index
      const combinedIndex = new Map();
      const allTerms = [...receiptIndex.keys(), ...supplierIndex.keys()];
      allTerms.forEach(term => {
        if (term && typeof term === 'string') {
          const results = [
            ...(receiptIndex.get(term) || []),
            ...(supplierIndex.get(term) || [])
          ];
          if (results.length > 0) {
            combinedIndex.set(term, results);
          }
        }
      });
      this.cache.set('search_purchases_combined_index', combinedIndex);

    } catch (error) {
      console.error('❌ [INSTANT CACHE] Error pre-calculating purchases search indexes:', error);
      // Set empty indexes on error
      this.cache.set('search_purchases_receipt_index', new Map());
      this.cache.set('search_purchases_supplier_index', new Map());
      this.cache.set('search_purchases_combined_index', new Map());
    }
  }

  /**
   * Calculate top suppliers for purchases
   */
  calculateTopSuppliers(purchases, suppliers) {
    const supplierTotals = new Map();
    
    purchases.forEach(purchase => {
      const supplierId = purchase.supplierId;
      if (!supplierTotals.has(supplierId)) {
        supplierTotals.set(supplierId, { totalAmount: 0, purchaseCount: 0 });
      }
      
      const supplier = supplierTotals.get(supplierId);
      supplier.totalAmount += purchase.total || 0;
      supplier.purchaseCount += 1;
    });
    
    return Array.from(supplierTotals.entries())
      .map(([supplierId, data]) => {
        const supplier = suppliers.find(s => s.supplierId === supplierId);
        return {
          supplierId,
          name: supplier?.name || '',
          totalAmount: data.totalAmount,
          purchaseCount: data.purchaseCount,
          averageValue: data.purchaseCount > 0 ? data.totalAmount / data.purchaseCount : 0
        };
      })
      .sort((a, b) => b.totalAmount - a.totalAmount);
  }

  /**
   * Calculate top products for purchases
   */
  calculateTopPurchaseProducts(purchases, products) {
    const productTotals = new Map();
    
    purchases.forEach(purchase => {
      const productId = purchase.productId;
      if (!productTotals.has(productId)) {
        productTotals.set(productId, { totalAmount: 0, totalQuantity: 0, purchaseCount: 0 });
      }
      
      const product = productTotals.get(productId);
      product.totalAmount += purchase.total || 0;
      product.totalQuantity += purchase.quantity || 0;
      product.purchaseCount += 1;
    });
    
    return Array.from(productTotals.entries())
      .map(([productId, data]) => {
        const product = products.find(p => p.id === productId);
        return {
          productId,
          name: product?.name || '',
          totalAmount: data.totalAmount,
          totalQuantity: data.totalQuantity,
          purchaseCount: data.purchaseCount,
          averageValue: data.purchaseCount > 0 ? data.totalAmount / data.purchaseCount : 0,
          averageQuantity: data.purchaseCount > 0 ? data.totalQuantity / data.purchaseCount : 0
        };
      })
      .sort((a, b) => b.totalQuantity - a.totalQuantity);
  }

  /**
   * Calculate monthly purchases trends
   */
  calculateMonthlyPurchasesTrends(purchases) {
    const monthlyData = new Map();
    
    purchases.forEach(purchase => {
      const date = new Date(purchase.date);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      
      if (!monthlyData.has(monthKey)) {
        monthlyData.set(monthKey, { totalAmount: 0, purchaseCount: 0 });
      }
      
      const month = monthlyData.get(monthKey);
      month.totalAmount += purchase.total || 0;
      month.purchaseCount += 1;
    });
    
    return Array.from(monthlyData.entries())
      .map(([month, data]) => ({
        month,
        totalAmount: data.totalAmount,
        purchaseCount: data.purchaseCount,
        averageValue: data.purchaseCount > 0 ? data.totalAmount / data.purchaseCount : 0
      }))
      .sort((a, b) => a.month.localeCompare(b.month));
  }

  /**
   * Calculate supplier analytics
   */
  calculateSupplierAnalytics(purchases, suppliers) {
    const analytics = new Map();
    
    suppliers.forEach(supplier => {
      const supplierPurchases = purchases.filter(p => p.supplierId === supplier.supplierId);
      const totalAmount = supplierPurchases.reduce((sum, p) => sum + (p.total || 0), 0);
      const totalItems = supplierPurchases.reduce((sum, p) => sum + (p.quantity || 0), 0);
      
      analytics.set(supplier.supplierId, {
        supplierId: supplier.supplierId,
        name: supplier.name,
        totalAmount,
        totalItems,
        purchaseCount: supplierPurchases.length,
        averageValue: supplierPurchases.length > 0 ? totalAmount / supplierPurchases.length : 0,
        averageItems: supplierPurchases.length > 0 ? totalItems / supplierPurchases.length : 0
      });
    });
    
    return analytics;
  }

  /**
   * Calculate product analytics
   */
  calculateProductAnalytics(purchases, products) {
    const analytics = new Map();
    
    products.forEach(product => {
      const productPurchases = purchases.filter(p => p.productId === product.id);
      const totalAmount = productPurchases.reduce((sum, p) => sum + (p.total || 0), 0);
      const totalQuantity = productPurchases.reduce((sum, p) => sum + (p.quantity || 0), 0);
      
      analytics.set(product.id, {
        productId: product.id,
        name: product.name,
        totalAmount,
        totalQuantity,
        purchaseCount: productPurchases.length,
        averageValue: productPurchases.length > 0 ? totalAmount / productPurchases.length : 0,
        averageQuantity: productPurchases.length > 0 ? totalQuantity / productPurchases.length : 0
      });
    });
    
    return analytics;
  }

  /**
   * Get default purchases analytics
   */
  getDefaultPurchasesAnalytics() {
    return {
      totalPurchases: 0,
      totalAmount: 0,
      averageValue: 0,
      totalItems: 0,
      averageItemsPerPurchase: 0,
      topSuppliers: [],
      topProducts: [],
      monthlyTrends: [],
      supplierAnalytics: new Map(),
      productAnalytics: new Map()
    };
  }

  // ===== PURCHASES DATA ACCESS METHODS =====

  /**
   * Get all purchases
   */
  getPurchases() {
    return this.cache.get('purchases') || [];
  }

  /**
   * Get purchases count
   */
  getPurchasesCount() {
    return this.cache.get('purchasesCount') || 0;
  }

  /**
   * Get sorted purchases
   */
  getSortedPurchases() {
    return this.cache.get('sortedPurchases') || [];
  }

  /**
   * Get grouped purchases
   */
  getGroupedPurchases() {
    return this.cache.get('groupedPurchases') || [];
  }

  /**
   * Get filtered purchases
   */
  getFilteredPurchases(filters) {
    const { searchType, searchValue, startDate, endDate } = filters;
    
    if (searchType === 'receipt' && searchValue) {
      return this.getPurchasesByReceipt(searchValue);
    }
    
    if (searchType === 'supplier' && searchValue) {
      return this.getPurchasesBySupplier(searchValue);
    }
    
    if (searchType === 'date' && searchValue) {
      return this.getPurchasesByDate(searchValue);
    }
    
    if (searchType === 'period' && startDate && endDate) {
      return this.getPurchasesByPeriod(startDate, endDate);
    }
    
    return this.getPurchases();
  }

  /**
   * Get purchases by receipt number
   */
  getPurchasesByReceipt(receiptNumber) {
    const index = this.cache.get('search_purchases_receipt_index');
    if (!index) return [];
    
    const term = receiptNumber.toLowerCase();
    const results = [];
    
    for (const [key, purchases] of index) {
      if (key.includes(term)) {
        results.push(...purchases);
      }
    }
    
    return results;
  }

  /**
   * Get purchases by supplier name or id (partial match on supplier name index).
   */
  getPurchasesBySupplier(supplierTerm) {
    const term = String(supplierTerm || '').toLowerCase().trim();
    if (!term) return [];

    const index = this.cache.get('search_purchases_supplier_index');
    const results = [];
    const seen = new Set();

    const pushUnique = (purchase) => {
      if (!purchase) return;
      const id = purchase.purchaseId || purchase.id;
      const key = id != null ? String(id) : JSON.stringify(purchase);
      if (seen.has(key)) return;
      seen.add(key);
      results.push(purchase);
    };

    if (index && typeof index.entries === 'function') {
      for (const [key, purchases] of index) {
        if (String(key).includes(term)) {
          (purchases || []).forEach(pushUnique);
        }
      }
    }

    const byId = this.cache.get(`filter_purchases_supplier_${supplierTerm}`);
    if (Array.isArray(byId)) {
      byId.forEach(pushUnique);
    }

    return results;
  }

  /**
   * Get purchases by date
   */
  getPurchasesByDate(date) {
    return this.cache.get(`filter_purchases_date_${date}`) || [];
  }

  /**
   * Get purchases by period
   */
  getPurchasesByPeriod(startDate, endDate) {
    const key = `filter_purchases_period_${startDate}_${endDate}`;
    let result = this.cache.get(key);
    
    if (!result) {
      const purchases = this.getPurchases();
      const start = new Date(startDate);
      const end = new Date(endDate);
      end.setHours(23, 59, 59);
      
      result = purchases.filter(p => {
        const purchaseDate = new Date(p.date);
        return purchaseDate >= start && purchaseDate <= end;
      });
      
      this.cache.set(key, result);
    }
    
    return result;
  }

  /**
   * Get purchases by product
   */
  getPurchasesByProduct(productId) {
    return this.cache.get(`filter_purchases_product_${productId}`) || [];
  }

  /**
   * Search purchases
   */
  searchPurchases(term) {
    const index = this.cache.get('search_purchases_combined_index');
    if (!index) return [];
    
    const searchTerm = term.toLowerCase();
    const results = [];
    
    for (const [key, purchases] of index) {
      if (key.includes(searchTerm)) {
        results.push(...purchases);
      }
    }
    
    return results;
  }

  // ===== PURCHASES ANALYTICS METHODS =====

  /**
   * Get purchases analytics
   */
  getPurchasesAnalytics() {
    return this.cache.get('purchasesAnalytics') || this.getDefaultPurchasesAnalytics();
  }

  /**
   * Get total purchases amount
   */
  getTotalPurchasesAmount() {
    return this.cache.get('totalPurchasesAmount') || 0;
  }

  /**
   * Get average purchase value
   */
  getAveragePurchaseValue() {
    return this.cache.get('averagePurchaseValue') || 0;
  }

  /**
   * Get top suppliers
   */
  getTopSuppliers(limit = 10) {
    const analytics = this.getPurchasesAnalytics();
    return (analytics.topSuppliers || []).slice(0, limit);
  }

  /**
   * Get supplier analytics
   */
  getSupplierAnalytics(supplierId) {
    const analytics = this.getPurchasesAnalytics();
    return analytics.supplierAnalytics?.get(supplierId) || {
      supplierId,
      totalAmount: 0,
      totalItems: 0,
      purchaseCount: 0,
      averageValue: 0,
      averageItems: 0
    };
  }

  /**
   * Get top products
   */
  getTopProducts(limit = 10) {
    const analytics = this.getPurchasesAnalytics();
    return (analytics.topProducts || []).slice(0, limit);
  }

  /**
   * Get product analytics
   */
  getProductAnalytics(productId) {
    const analytics = this.getPurchasesAnalytics();
    return analytics.productAnalytics?.get(productId) || {
      productId,
      totalAmount: 0,
      totalQuantity: 0,
      purchaseCount: 0,
      averageValue: 0,
      averageQuantity: 0
    };
  }

  /**
   * Get monthly purchases
   */
  getMonthlyPurchases() {
    const analytics = this.getPurchasesAnalytics();
    return analytics.monthlyTrends || [];
  }

  /**
   * Get purchases trends
   */
  getPurchasesTrends(period = 'monthly') {
    const analytics = this.getPurchasesAnalytics();
    return analytics[`${period}Trends`] || [];
  }

  // ===== PURCHASES CACHE UPDATE METHODS =====

  /**
   * Update sales data
   * @param {Array} sales
   * @param {{ warmWorker?: boolean, lightAnalytics?: boolean }} [options]
   *   warmWorker: rebuild per-worker dashboards (O(workers×sales)) — POS/worker surfaces only
   *   lightAnalytics: KPI totals only (default true) — skip topCustomers/topProducts/dailySales
   */
  updateSalesData(sales, options = {}) {
    const warmWorker = options.warmWorker === true;
    const lightAnalytics = options.lightAnalytics !== false;

    this.cache.set('salesCount', Array.isArray(sales) ? sales.length : 0);

    for (const key of Array.from(this.cache.keys())) {
      if (typeof key === 'string' && key.startsWith('filter_sales')) {
        this.cache.delete(key);
      }
    }

    const customers = this.cache.get('customers') || [];
    const products = this.cache.get('products') || [];

    this.preCalculateSalesData({
      sales,
      customers,
      products,
      lightAnalytics
    });

    // Do not warm worker dashboards from Sales list refresh — that is O(workers×sales)
    if (warmWorker) {
      this.preCalculateWorkerDashboard({
        sales,
        discrepancyManagement:
          this.cache.get('discrepancyManagement') || {
            selfDeviations: [],
            handoverDeviations: []
          }
      });
    }

    // Drop raw history after derived KPIs — DataContext is SSOT for the list.
    this.cache.delete('sales');

    publishLegacyWindowEvent('salesCacheUpdated');

  }

  /**
   * Update purchases data
   */
  updatePurchasesData(purchases) {
    this.cache.set('purchases', purchases);
    this.cache.set('purchasesCount', purchases.length);
    
    // Re-calculate all purchases data
    const suppliers = this.cache.get('suppliers') || [];
    const products = this.cache.get('products') || [];
    const lots = this.cache.get('lots') || {};
    
    this.preCalculatePurchasesData({
      purchases,
      suppliers,
      products,
      lots
    });

    const affectedProductIds = [
      ...new Set(
        (purchases || [])
          .flatMap((p) => {
            if (p?.productId != null) return [p.productId];
            return (p?.products || []).map((line) => line.productId).filter(Boolean);
          })
          .filter(Boolean)
      )
    ];
    this._refreshProductPricingAfterDataChange(lots, purchases, affectedProductIds);

    // Dispatch update event
    publishLegacyWindowEvent('purchasesCacheUpdated');

  }

  /**
   * Update transfers data (unified with purchases/sales pattern)
   */
  updateTransfersData(transferData) {
    // Update lots data from transfer
    if (transferData.lots) {
      const lotsArray = Array.isArray(transferData.lots) ? transferData.lots : Object.values(transferData.lots);
      this.cache.set('lots', lotsArray);
      if (!isCoordinatedStockRecalculationActive()) {
        this.updatePartialData({ lots: lotsArray });
      }
    }
    
    if (!isCoordinatedStockRecalculationActive()) {
      const products = this.cache.get('products') || [];
      const lots = this.cache.get('lots') || [];
      const purchases = this.cache.get('purchases') || [];
      const stockLocations = this.cache.get('stockLocations') || ['Principal'];

      this.preCalculateStockData({
        products,
        lots,
        purchases,
        stockLocations
      });
    }
    
    // Dispatch update event (same pattern as purchases/sales)
    publishLegacyWindowEvent('transfersCacheUpdated');
    publishLegacyWindowEvent('stockUpdated', {
      action: 'transferCompleted',
      transferData: transferData,
      timestamp: Date.now()
    });
  }

  /**
   * Update suppliers data
   */
  updateSuppliersData(suppliers) {
    this.cache.set('suppliers', suppliers);
    
    // Re-calculate supplier-related cache
    const purchases = this.cache.get('purchases') || [];
    this.preCalculatePurchasesFilters(purchases, suppliers);
    this.preCalculatePurchasesSearchIndexes(purchases, suppliers);
    
    // Dispatch update event
    publishLegacyWindowEvent('suppliersCacheUpdated');
  }

  /**
   * Update products data
   */
  updateProductsData(products) {
    this.cache.set('products', products);

    // Keep inventory filter_ in sync (otherwise read model prefers stale filter_)
    try {
      this.lastFilterHash = null;
      this.preCalculateFilters(products || []);
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('[InstantCache] filter refresh after products update skipped:', err?.message);
      }
    }

    // Re-calculate product-related cache
    const purchases = this.cache.get('purchases') || [];
    const suppliers = this.cache.get('suppliers') || [];

    this.preCalculatePurchasesFilters(purchases, suppliers);

    // Dispatch update event
    publishLegacyWindowEvent('productsCacheUpdated');
  }

  /**
   * Refresh stock locations list.
   * @param {{ rebuildStock?: boolean }} [opts] rebuildStock=true clears stockData and recalculates
   *   (location add/remove). Default is list-only — Stock page mount must NOT wipe the RM.
   */
  refreshStockLocations(opts = {}) {
    const rebuildStock = Boolean(opts.rebuildStock);

    this.cache.delete('stockLocations');
    this.cache.delete('uniqueLocations');
    this.cache.delete('locationStockData');
    this.cache.delete('purchasedProductIdSet');

    // Repopulate location list from lots (cached for subsequent getStockLocations)
    this.getStockLocations();

    if (!rebuildStock) {
      return;
    }

    this.cache.delete('stockData');
    this.dataVersion++;
    this.refreshStockDataCache();
    publishInstantCacheUpdated();
  }
}

// Create singleton instance
const instantCache = new InstantCache();

export default instantCache;
